import React, { useState, useRef, useEffect } from 'react';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ChannelLinkHealthCard from '../components/admin/ChannelLinkHealthCard';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { AdminExchangeRatesTab } from '@/components/admin/AdminExchangeRatesTab';
import { AdminActivityLogsTab } from '@/components/admin/AdminActivityLogsTab';
import { AdminSecurityTab } from '@/components/admin/AdminSecurityTab';
import { AdminReportsTab } from '@/components/admin/AdminReportsTab';
import AdminLeaderboardTab from '@/components/admin/AdminLeaderboardTab';
import AdminBrokerCleanupTab from '@/components/admin/AdminBrokerCleanupTab';
import AdminScorecardTab from '@/components/admin/AdminScorecardTab';
import MTProtoHealthCard from '@/components/admin/MTProtoHealthCard';
import MtprotoConnectionHistory from '@/components/admin/MtprotoConnectionHistory';
import DataQualityDashboard from '@/components/admin/DataQualityDashboard';
import DataRecoveryDashboard from '@/components/admin/DataRecoveryDashboard';
import AdminDailyDataImportTab from '@/components/admin/AdminDailyDataImportTab';
import AdminBackupTab from '@/components/admin/AdminBackupTab';
import { 
  Settings, BarChart3, Users, Key, Cpu, Upload, Brain, 
  Shield, Lock, Save, Trash2, Plus, FileText, Sparkles,
  ChevronRight, AlertTriangle, Check, Eye, EyeOff, RefreshCw,
  Link2, Database, Bell, Activity, Download, UserPlus, Edit,
  Globe, Calendar, Hash, Zap, TestTube, ExternalLink, Clock,
  Search, Filter, MoreVertical, CheckCircle2, XCircle, Loader2,
  Calculator, Target, TrendingUp, Percent, ArrowRight, ChevronDown, Rocket,
  ChevronUp, Copy, Gauge, DollarSign, Banknote, Heart, Smile, Frown, Meh, Send, Trophy
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Telegram Reports Tab
function KpiThresholdsTab() {
  const [thresholds, setThresholds] = useState(null);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetch = async () => {
      try {
        const r = await axios.get(`${API}/admin/kpi-thresholds`, { headers: { Authorization: `Bearer ${token}` } });
        setThresholds(r.data.thresholds);
      } catch {}
    };
    fetch();
  }, [token]);

  const updateValue = (dept, metric, level, val) => {
    setThresholds(prev => ({
      ...prev,
      [dept]: { ...prev[dept], [metric]: { ...prev[dept][metric], [level]: parseFloat(val) || 0 } }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/kpi-thresholds`, { thresholds }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('KPI thresholds saved — auto-applied to all IBs');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  if (!thresholds) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-[hsl(var(--primary))]" /></div>;

  const DEPT_CONFIG = {
    marketing: { label: 'Marketing (Ads Performance)', color: '#E63946' },
    sales: { label: 'Sales Performance', color: '#10B981' },
    operation: { label: 'Operation Performance', color: '#3B82F6' },
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-white">KPI Thresholds</h3>
          <p className="text-xs text-[hsl(var(--text-muted))]">Set global thresholds — auto-applies to all IB Channel Performance cards, War Room, and Telegram reports</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-[#E63946] to-[#F97316] text-white text-xs">
          {saving ? <Loader2 size={13} className="animate-spin mr-1" /> : <Check size={13} className="mr-1" />} Save All
        </Button>
      </div>

      {Object.entries(DEPT_CONFIG).map(([dept, config]) => (
        <div key={dept} className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[hsl(var(--surface-elevated))] to-transparent">
            <h4 className="text-xs font-bold" style={{ color: config.color }}>{config.label}</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[hsl(var(--border))]">
                  <th className="py-2 px-4 text-left text-[9px] uppercase text-[hsl(var(--text-muted))]">Metric</th>
                  <th className="py-2 px-3 text-center text-[9px] uppercase text-[#10B981]">Excellent</th>
                  <th className="py-2 px-3 text-center text-[9px] uppercase text-[#3B82F6]">Good</th>
                  <th className="py-2 px-3 text-center text-[9px] uppercase text-[#F59E0B]">Average</th>
                  <th className="py-2 px-3 text-center text-[9px] uppercase text-[#6B7280]">Poor</th>
                  <th className="py-2 px-3 text-center text-[9px] uppercase text-[hsl(var(--text-muted))]">Unit</th>
                  <th className="py-2 px-3 text-center text-[9px] uppercase text-[hsl(var(--text-muted))]">Direction</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(thresholds[dept] || {}).map(([metric, vals]) => (
                  <tr key={metric} className="border-b border-[hsl(var(--border))]/30 hover:bg-[hsl(var(--surface-elevated))]/50">
                    <td className="py-2 px-4 font-semibold text-white text-[11px]">{metric.replace(/_/g, ' ')}</td>
                    <td className="py-2 px-3"><input type="number" step="any" value={vals.excellent} onChange={e => updateValue(dept, metric, 'excellent', e.target.value)} className="w-full h-7 text-center text-[11px] bg-[#10B981]/10 border border-[#10B981]/30 rounded text-[#10B981] outline-none" /></td>
                    <td className="py-2 px-3"><input type="number" step="any" value={vals.good} onChange={e => updateValue(dept, metric, 'good', e.target.value)} className="w-full h-7 text-center text-[11px] bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded text-[#3B82F6] outline-none" /></td>
                    <td className="py-2 px-3"><input type="number" step="any" value={vals.average} onChange={e => updateValue(dept, metric, 'average', e.target.value)} className="w-full h-7 text-center text-[11px] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded text-[#F59E0B] outline-none" /></td>
                    <td className="py-2 px-3 text-center text-[10px] text-[#E63946]">{'>'}{vals.lower_better ? vals.average : '<'}{!vals.lower_better ? vals.average : ''}</td>
                    <td className="py-2 px-3 text-center text-[10px] text-[hsl(var(--text-muted))]">{vals.unit || '-'}</td>
                    <td className="py-2 px-3 text-center text-[10px] text-[hsl(var(--text-muted))]">{vals.lower_better ? 'Lower better' : 'Higher better'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function TelegramReportsTab() {
  const [logs, setLogs] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [reportDate, setReportDate] = useState('');
  const [logFilter, setLogFilter] = useState('all');
  const token = localStorage.getItem('token');
  const h = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => { fetchAll(); }, []);
  const fetchAll = async () => {
    try {
      const [logRes, chRes] = await Promise.all([
        axios.get(`${API}/reports/logs`, h),
        axios.get(`${API}/reports/channels`, h),
      ]);
      setLogs(logRes.data.logs || []);
      setChannels(chRes.data.channels || []);
    } catch {} finally { setLoading(false); }
  };

  const toggleChannel = (id) => {
    setSelectedChannels(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };
  const selectAllChannels = () => {
    const connected = channels.filter(c => c.connected).map(c => c.ib_id);
    setSelectedChannels(prev => prev.length === connected.length ? [] : connected);
  };
  const toggleType = (t) => {
    setSelectedTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  const sendCustom = async () => {
    if (selectedChannels.length === 0 || selectedTypes.length === 0) {
      toast.error('Pilih channel dan jenis message');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/reports/send-custom`, {
        channel_ids: selectedChannels,
        message_types: selectedTypes,
        ...(reportDate ? { report_date: reportDate } : {}),
      }, h);
      const total = (res.data.results || []).reduce((s, r) => s + (r.messages_sent || 0), 0);
      toast.success(`${total} messages sent to ${res.data.results?.filter(r => r.messages_sent > 0).length || 0} channels`);
      fetchAll();
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  const triggerBulk = async (type) => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/reports/send-${type}`, {}, h);
      toast.success(`${type} reports sent to ${res.data.sent} channels`);
      fetchAll();
    } catch { toast.error('Failed'); }
    finally { setSending(false); }
  };

  const triggerMTProtoSync = async (channelType) => {
    setSending(true);
    try {
      const url = channelType ? `${API}/mtproto/sync-all?channel_type=${channelType}` : `${API}/mtproto/sync-all`;
      const res = await axios.post(url, {}, h);
      if (res.data.success) {
        toast.success(`MTProto synced ${res.data.synced}/${res.data.total} channels`);
      } else if (res.data.needs_auth) {
        toast.error('MTProto needs re-authentication. Go to Integrations tab.');
      } else {
        toast.error(res.data.error || 'Sync failed');
      }
    } catch { toast.error('MTProto sync failed'); }
    finally { setSending(false); }
  };

  const msgTypeOptions = [
    { id: 'sales', label: 'Sales', color: '#10b981', icon: '💼' },
    { id: 'marketing', label: 'Marketing', color: '#3b82f6', icon: '📢' },
    { id: 'operations', label: 'Operations', color: '#f59e0b', icon: '⚙️' },
    { id: 'performance', label: 'Performance Metrics', color: '#8b5cf6', icon: '📊' },
    { id: 'issues', label: 'Issues & Optimization', color: '#ef4444', icon: '🚨' },
    { id: 'wins', label: 'Wins & Highlights', color: '#10b981', icon: '🏆' },
    { id: 'weekly', label: 'Weekly Summary', color: '#8b5cf6', icon: '📈' },
    { id: 'monthly', label: 'Monthly Summary', color: '#ec4899', icon: '📊' },
  ];

  const filteredLogs = logFilter === 'all' ? logs : logs.filter(l => l.type === logFilter);

  return (
    <div className="space-y-5">
      {/* CHANNELS */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
          <div className="relative flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40"><Send size={18} className="text-white" /></div>
            <div><h3 className="text-base font-bold text-white">Telegram Report Control</h3><p className="text-[10px] text-[#9CA3AF]">Select channels & message types, then send</p></div>
          </div>
        </div>
        <div className="p-5">
          {/* Channel Selection */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider">Select Channels</span>
              <button onClick={selectAllChannels} className="text-[10px] text-[#3b82f6] font-semibold hover:underline">
                {selectedChannels.length === channels.filter(c => c.connected).length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
              {channels.map(ch => (
                <button key={ch.ib_id} disabled={!ch.connected}
                  onClick={() => toggleChannel(ch.ib_id)}
                  className={`p-2.5 rounded-lg border text-left transition-all ${!ch.connected ? 'opacity-30 cursor-not-allowed border-[hsl(var(--border))]' : selectedChannels.includes(ch.ib_id) ? 'border-[#E63946] bg-[#E63946]/10' : 'border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] hover:border-[hsl(var(--border-hover))]'}`}
                  data-testid={`ch-select-${ch.ib_id}`}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className={`w-2 h-2 rounded-full ${ch.connected ? 'bg-[#10b981]' : 'bg-[#ef4444]'}`} />
                    <span className="text-[11px] font-semibold text-white truncate">{ch.name}</span>
                  </div>
                  {ch.last_report && <span className="text-[8px] text-[hsl(var(--text-muted))]">Last: {new Date(ch.last_report).toLocaleDateString('en-MY', { day: '2-digit', month: 'short' })}</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Message Type Selection */}
          <div className="mb-4">
            <span className="text-[10px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider block mb-2">Message Type</span>
            <div className="flex flex-wrap gap-2">
              {msgTypeOptions.map(t => (
                <button key={t.id} onClick={() => toggleType(t.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold border transition-all ${selectedTypes.includes(t.id) ? `border-[${t.color}] text-white` : 'border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:text-white'}`}
                  style={selectedTypes.includes(t.id) ? { borderColor: t.color, backgroundColor: `${t.color}20`, color: t.color } : {}}
                  data-testid={`type-select-${t.id}`}
                >
                  <span>{t.icon}</span> {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Report Date Selection */}
          <div className="mb-4">
            <span className="text-[10px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider block mb-2">Report Date</span>
            <div className="flex items-center gap-3">
              <input
                type="date"
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
                className="px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] text-white text-xs"
                data-testid="report-date-picker"
              />
              <span className="text-[10px] text-[hsl(var(--text-muted))]">
                {reportDate ? `Data untuk ${new Date(reportDate + 'T00:00:00').toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' })}` : 'Default: semalam'}
              </span>
              {reportDate && (
                <button onClick={() => setReportDate('')} className="text-[10px] text-[#ef4444] hover:underline">Reset</button>
              )}
            </div>
          </div>

          {/* Send buttons */}
          <div className="flex items-center gap-3 pt-3 border-t border-[hsl(var(--border))]">
            <button onClick={sendCustom} disabled={sending || selectedChannels.length === 0 || selectedTypes.length === 0}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#E63946] text-white text-xs font-semibold hover:bg-[#d32f3f] disabled:opacity-40 transition-all"
              data-testid="send-custom-btn"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Send to {selectedChannels.length} channel{selectedChannels.length !== 1 ? 's' : ''}
            </button>
            <span className="text-[10px] text-[hsl(var(--text-muted))]">or bulk:</span>
            <button onClick={() => triggerBulk('daily')} disabled={sending} className="px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-[#10b981]/15 text-[#10b981] border border-[#10b981]/30 hover:bg-[#10b981]/25 disabled:opacity-40">All Daily</button>
            <button onClick={() => triggerBulk('weekly')} disabled={sending} className="px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-[#3b82f6]/15 text-[#3b82f6] border border-[#3b82f6]/30 hover:bg-[#3b82f6]/25 disabled:opacity-40">All Weekly</button>
            <button onClick={() => triggerBulk('monthly')} disabled={sending} className="px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-[#8b5cf6]/15 text-[#8b5cf6] border border-[#8b5cf6]/30 hover:bg-[#8b5cf6]/25 disabled:opacity-40">All Monthly</button>
          </div>

          {/* MTProto Sync Row */}
          <div className="flex items-center gap-3 pt-3 border-t border-[hsl(var(--border))] mt-3">
            <div className="flex flex-col">
              <span className="text-[11px] font-bold text-white">MTProto VIP Engagement Sync</span>
              <span className="text-[9px] text-[hsl(var(--text-muted))]">Fetch latest subscriber count, views, reactions for all VIP/Public channels</span>
            </div>
            <div className="ml-auto flex gap-2">
              <button onClick={() => triggerMTProtoSync('vip')} disabled={sending} data-testid="mtproto-sync-vip-btn"
                className="px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-[#a855f7]/15 text-[#a855f7] border border-[#a855f7]/30 hover:bg-[#a855f7]/25 disabled:opacity-40">
                {sending ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}Sync VIP
              </button>
              <button onClick={() => triggerMTProtoSync('public')} disabled={sending} data-testid="mtproto-sync-public-btn"
                className="px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-[#06b6d4]/15 text-[#06b6d4] border border-[#06b6d4]/30 hover:bg-[#06b6d4]/25 disabled:opacity-40">
                {sending ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}Sync Public
              </button>
              <button onClick={() => triggerMTProtoSync(null)} disabled={sending} data-testid="mtproto-sync-all-btn"
                className="px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-[#E63946]/15 text-[#E63946] border border-[#E63946]/30 hover:bg-[#E63946]/25 disabled:opacity-40">
                {sending ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}Sync All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MTProto Health & Alerts */}
      <MTProtoHealthCard />

      {/* MTProto Connection History — last 7 days timeline */}
      <MtprotoConnectionHistory />

      {/* HISTORY */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <h4 className="text-sm font-bold text-white">Report History</h4>
          <div className="flex gap-1">
            {['all', 'daily', 'weekly', 'monthly', 'custom', 'test'].map(f => (
              <button key={f} onClick={() => setLogFilter(f)}
                className={`px-2 py-1 rounded text-[9px] font-semibold transition-all ${logFilter === f ? 'bg-[#E63946] text-white' : 'text-[hsl(var(--text-muted))] hover:text-white'}`}
              >{f.toUpperCase()}</button>
            ))}
          </div>
        </div>
        <div className="p-4 max-h-[350px] overflow-y-auto">
          {loading ? <Loader2 size={16} className="animate-spin text-[hsl(var(--text-muted))]" /> : (
            <div className="space-y-1.5">
              {filteredLogs.length === 0 ? <p className="text-xs text-[hsl(var(--text-muted))] py-4 text-center">No reports found</p> : filteredLogs.map((log, i) => {
                const colors = { daily: '#10b981', weekly: '#3b82f6', monthly: '#8b5cf6', custom: '#E63946', test: '#f59e0b' };
                const c = colors[log.type] || '#6b7280';
                return (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]">
                    <Badge className="text-[8px] font-bold" style={{ backgroundColor: `${c}15`, color: c, border: `1px solid ${c}40` }}>{log.type?.toUpperCase()}</Badge>
                    <span className="text-xs text-white flex-1">
                      {log.channel ? `${log.channel} — ${log.messages_sent || 0} msgs` : `${log.sent_count || 0} channels`}
                      {log.msg_types ? ` [${log.msg_types.join(', ')}]` : ''}
                    </span>
                    <span className="text-[10px] text-[hsl(var(--text-muted))]">{new Date(log.sent_at).toLocaleString('en-MY', { dateStyle: 'short', timeStyle: 'short' })}</span>
                    <CheckCircle2 size={12} className="text-[#10b981]" />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Auto schedule info */}
      <p className="text-[10px] text-[hsl(var(--text-muted))] text-center">Auto schedule: Daily 12:00 PM • Weekly Monday 12:00 PM • Monthly 1st 12:00 PM (GMT+8)</p>
    </div>
  );
}

// Section Header Component
function SectionHeader({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
            <Icon size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white font-heading">{title}</h3>
            <p className="text-xs text-[hsl(var(--text-muted))]">{subtitle}</p>
          </div>
        </div>
        {action}
      </div>
    </div>
  );
}

// Status Badge Component
function StatusBadge({ status }) {
  const config = {
    connected: { bg: 'bg-[#10B981]/20', text: 'text-[#10B981]', label: 'Connected' },
    not_connected: { bg: 'bg-[#6B7280]/20', text: 'text-[#6B7280]', label: 'Not Connected' },
    active: { bg: 'bg-[#10B981]/20', text: 'text-[#10B981]', label: 'Active' },
    inactive: { bg: 'bg-[#EF4444]/20', text: 'text-[#EF4444]', label: 'Inactive' },
    pending: { bg: 'bg-[#F59E0B]/20', text: 'text-[#F59E0B]', label: 'Pending' },
  };
  const c = config[status] || config.not_connected;
  return <Badge className={`${c.bg} ${c.text} text-[10px]`}>{c.label}</Badge>;
}

// Mood Analytics Section Component
function MoodAnalyticsSection() {
  const [moodStats, setMoodStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7'); // days

  useEffect(() => {
    fetchMoodStats();
  }, [period]);

  const fetchMoodStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/mood-analytics?days=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMoodStats(res.data);
    } catch (err) {
      console.error('Failed to fetch mood stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const moodConfig = {
    great: { label: 'Great', emoji: '🔥', color: 'bg-emerald-500', textColor: 'text-emerald-400' },
    good: { label: 'Good', emoji: '😊', color: 'bg-blue-500', textColor: 'text-blue-400' },
    okay: { label: 'Okay', emoji: '😐', color: 'bg-amber-500', textColor: 'text-amber-400' },
    stressed: { label: 'Stressed', emoji: '😰', color: 'bg-orange-500', textColor: 'text-orange-400' },
    bad: { label: 'Bad', emoji: '😞', color: 'bg-red-500', textColor: 'text-red-400' },
  };

  const calculatePercentage = (count, total) => total > 0 ? Math.round((count / total) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-[hsl(var(--primary))]" />
      </div>
    );
  }

  const totalMoods = moodStats?.total || 0;
  const moodCounts = moodStats?.by_mood || {};
  const dailyStats = moodStats?.daily || [];
  const userStats = moodStats?.by_user || [];

  // Calculate wellness score (weighted average: great=5, good=4, okay=3, stressed=2, bad=1)
  const wellnessScore = totalMoods > 0 
    ? Math.round(((moodCounts.great || 0) * 5 + (moodCounts.good || 0) * 4 + (moodCounts.okay || 0) * 3 + (moodCounts.stressed || 0) * 2 + (moodCounts.bad || 0) * 1) / totalMoods * 20)
    : 0;

  return (
    <div className="space-y-6" data-testid="mood-analytics-section">
      {/* Header with Period Filter */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/30">
            <Heart size={24} className="text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Team Mood Analytics</h2>
            <p className="text-sm text-[hsl(var(--text-muted))]">Monitor team wellness and happiness trends</p>
          </div>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40 bg-[hsl(var(--surface))] border-[hsl(var(--border))]">
            <Calendar size={14} className="mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <SelectItem value="7">Last 7 Days</SelectItem>
            <SelectItem value="14">Last 14 Days</SelectItem>
            <SelectItem value="30">Last 30 Days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-violet-500/10 to-purple-500/5 border border-violet-500/20 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Heart size={16} className="text-violet-400" />
            <span className="text-sm text-[hsl(var(--text-muted))]">Wellness Score</span>
          </div>
          <p className="text-3xl font-bold text-white">{wellnessScore}%</p>
          <div className="w-full h-2 bg-[hsl(var(--surface))] rounded-full mt-2 overflow-hidden">
            <div className={`h-full transition-all ${wellnessScore >= 70 ? 'bg-emerald-500' : wellnessScore >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${wellnessScore}%` }} />
          </div>
        </div>
        
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Activity size={16} className="text-blue-400" />
            <span className="text-sm text-[hsl(var(--text-muted))]">Total Check-ins</span>
          </div>
          <p className="text-3xl font-bold text-white">{totalMoods}</p>
          <p className="text-xs text-[hsl(var(--text-muted))] mt-1">in {period} days</p>
        </div>
        
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Smile size={16} className="text-emerald-400" />
            <span className="text-sm text-[hsl(var(--text-muted))]">Positive Moods</span>
          </div>
          <p className="text-3xl font-bold text-emerald-400">
            {calculatePercentage((moodCounts.great || 0) + (moodCounts.good || 0), totalMoods)}%
          </p>
          <p className="text-xs text-[hsl(var(--text-muted))] mt-1">Great + Good</p>
        </div>
        
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Frown size={16} className="text-red-400" />
            <span className="text-sm text-[hsl(var(--text-muted))]">Needs Attention</span>
          </div>
          <p className="text-3xl font-bold text-red-400">
            {calculatePercentage((moodCounts.stressed || 0) + (moodCounts.bad || 0), totalMoods)}%
          </p>
          <p className="text-xs text-[hsl(var(--text-muted))] mt-1">Stressed + Bad</p>
        </div>
      </div>

      {/* Mood Distribution */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))]">
          <h3 className="font-semibold flex items-center gap-2">
            <BarChart3 size={16} className="text-violet-400" />
            Mood Distribution
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-5 gap-4">
            {Object.entries(moodConfig).map(([key, config]) => (
              <div key={key} className="text-center">
                <div className="text-4xl mb-2">{config.emoji}</div>
                <div className={`text-2xl font-bold ${config.textColor}`}>{moodCounts[key] || 0}</div>
                <div className="text-xs text-[hsl(var(--text-muted))]">{config.label}</div>
                <div className="text-xs text-[hsl(var(--text-muted))] mt-1">{calculatePercentage(moodCounts[key] || 0, totalMoods)}%</div>
                <div className="w-full h-2 bg-[hsl(var(--background))] rounded-full mt-2 overflow-hidden">
                  <div className={`h-full ${config.color}`} style={{ width: `${calculatePercentage(moodCounts[key] || 0, totalMoods)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Daily Trend & Team Member Stats */}
      <div className="grid grid-cols-2 gap-6">
        {/* Daily Trend */}
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[hsl(var(--border))]">
            <h3 className="font-semibold flex items-center gap-2">
              <TrendingUp size={16} className="text-blue-400" />
              Daily Trend
            </h3>
          </div>
          <div className="p-6 max-h-64 overflow-y-auto">
            {dailyStats.length > 0 ? (
              <div className="space-y-3">
                {dailyStats.map((day, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <span className="text-xs text-[hsl(var(--text-muted))] w-20">{day.date}</span>
                    <div className="flex-1 flex gap-1 h-6">
                      {Object.entries(moodConfig).map(([key, config]) => (
                        day[key] > 0 && (
                          <div 
                            key={key} 
                            className={`${config.color} rounded h-full flex items-center justify-center text-white text-[10px] font-bold`}
                            style={{ width: `${calculatePercentage(day[key], day.total)}%`, minWidth: day[key] > 0 ? '20px' : '0' }}
                            title={`${config.label}: ${day[key]}`}
                          >
                            {day[key]}
                          </div>
                        )
                      ))}
                    </div>
                    <span className="text-xs text-[hsl(var(--text-muted))]">{day.total}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-[hsl(var(--text-muted))] py-4">No data available</p>
            )}
          </div>
        </div>

        {/* Team Member Stats */}
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[hsl(var(--border))]">
            <h3 className="font-semibold flex items-center gap-2">
              <Users size={16} className="text-emerald-400" />
              Team Member Wellness
            </h3>
          </div>
          <div className="p-6 max-h-64 overflow-y-auto">
            {userStats.length > 0 ? (
              <div className="space-y-3">
                {userStats.map((user, idx) => {
                  const userWellness = user.total > 0 
                    ? Math.round(((user.great || 0) * 5 + (user.good || 0) * 4 + (user.okay || 0) * 3 + (user.stressed || 0) * 2 + (user.bad || 0) * 1) / user.total * 20)
                    : 0;
                  return (
                    <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[hsl(var(--background))] transition-colors">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                        {user.user_name?.charAt(0) || '?'}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{user.user_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-1.5 bg-[hsl(var(--background))] rounded-full overflow-hidden">
                            <div className={`h-full ${userWellness >= 70 ? 'bg-emerald-500' : userWellness >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${userWellness}%` }} />
                          </div>
                          <span className={`text-xs font-medium ${userWellness >= 70 ? 'text-emerald-400' : userWellness >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                            {userWellness}%
                          </span>
                        </div>
                      </div>
                      <span className="text-xs text-[hsl(var(--text-muted))]">{user.total} check-ins</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-[hsl(var(--text-muted))] py-4">No data available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminPanelPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showPinChange, setShowPinChange] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [ibs, setIbs] = useState([]);
  const [systemSettings, setSystemSettings] = useState({});
  const [dbStats, setDbStats] = useState({});
  const [notifications, setNotifications] = useState([]);
  
  // Modal states
  const [showUserModal, setShowUserModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [showIBModal, setShowIBModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [selectedIB, setSelectedIB] = useState(null);
  
  // New user form
  const [newUserForm, setNewUserForm] = useState({ name: '', email: '', password: '', role_id: 'member' });

  // AI Brain state
  const [kitabFile, setKitabFile] = useState(null);
  const [kitabUploaded, setKitabUploaded] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [showPermModal, setShowPermModal] = useState(false);
  const [permEditUser, setPermEditUser] = useState(null);
  const [ibList, setIbList] = useState([]);
  const [permTeamExpanded, setPermTeamExpanded] = useState(false);
  
  // KPI Configuration States
  const [healthScoreWeights, setHealthScoreWeights] = useState({
    marketing: { targetAchievement: 25, conversionFunnel: 25, conversionRates: 25, adsEfficiency: 25 },
    sales: { targetAchievement: 30, pipeline: 25, closingRate: 25, commission: 20 },
    operations: { targetAchievement: 30, engagement: 25, activity: 25, retention: 20 },
  });
  
  const [conversionThresholds, setConversionThresholds] = useState({
    ctaConversion: { poor: 10, average: 20, good: 35, excellent: 50 },
    subsConversion: { poor: 5, average: 15, good: 30, excellent: 45 },
    leadsConversion: { poor: 15, average: 25, good: 40, excellent: 55 },
    closingRate: { poor: 20, average: 35, good: 50, excellent: 65 },
    depositRate: { poor: 30, average: 50, good: 70, excellent: 85 },
  });
  
  const [formulas, setFormulas] = useState([
    { id: 'cpm', name: 'CPM', formula: 'Ad Spent ÷ Impressions × 1000', variables: ['ad_spent', 'impressions'], multiplier: 1000, enabled: true, benchmarks: { poor: 50, average: 30, good: 15, excellent: 10 } },
    { id: 'ctr', name: 'CTR', formula: 'Clicks ÷ Impressions × 100', variables: ['clicks', 'impressions'], multiplier: 100, suffix: '%', enabled: true, benchmarks: { poor: 0.5, average: 1, good: 2, excellent: 3 } },
    { id: 'cplc', name: 'CPLC', formula: 'Ad Spent ÷ Clicks', variables: ['ad_spent', 'clicks'], enabled: true, benchmarks: { poor: 5, average: 3, good: 1.5, excellent: 0.8 } },
    { id: 'cplpv', name: 'CPLPV', formula: 'Ad Spent ÷ LPV', variables: ['ad_spent', 'lpv'], enabled: true, benchmarks: { poor: 3, average: 2, good: 1, excellent: 0.5 } },
    { id: 'cpb', name: 'CPB', formula: 'Ad Spent ÷ CTA Clicks', variables: ['ad_spent', 'cta_clicks'], enabled: true, benchmarks: { poor: 10, average: 6, good: 3, excellent: 1.5 } },
    { id: 'cps', name: 'CPS', formula: 'Ad Spent ÷ Subscribers', variables: ['ad_spent', 'subscribers'], enabled: true, benchmarks: { poor: 50, average: 30, good: 15, excellent: 8 } },
    { id: 'cpl', name: 'CPL', formula: 'Ad Spent ÷ Leads', variables: ['ad_spent', 'leads'], enabled: true, benchmarks: { poor: 100, average: 60, good: 35, excellent: 20 } },
    { id: 'cpc_client', name: 'CPC (Client)', formula: 'Ad Spent ÷ Closing', variables: ['ad_spent', 'closing'], enabled: true, benchmarks: { poor: 300, average: 150, good: 80, excellent: 50 } },
  ]);
  
  const [customFormulas, setCustomFormulas] = useState([
    { id: 'custom_1', name: '', formula: '', variables: [], enabled: false, benchmarks: { poor: 0, average: 0, good: 0, excellent: 0 } },
    { id: 'custom_2', name: '', formula: '', variables: [], enabled: false, benchmarks: { poor: 0, average: 0, good: 0, excellent: 0 } },
  ]);
  
  const [expandedSection, setExpandedSection] = useState('healthScore');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes, intRes, ibsRes, settingsRes, statsRes, notifRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/roles`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/integrations`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/ib/list`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/settings`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/notifications`, { headers }).catch(() => ({ data: [] })),
      ]);
      
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setIntegrations(intRes.data);
      setIbs(ibsRes.data);
      setIbList(Array.isArray(ibsRes.data) ? ibsRes.data : []);
      setSystemSettings(settingsRes.data);
      setDbStats(statsRes.data);
      setNotifications(notifRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle PIN change
  const handlePinChange = () => {
    if (newPin.length < 4) {
      toast.error('PIN mesti sekurang-kurangnya 4 digit');
      return;
    }
    if (newPin !== confirmPin) {
      toast.error('PIN tidak sepadan');
      return;
    }
    localStorage.setItem('adminPin', newPin);
    toast.success('PIN berjaya dikemaskini');
    setShowPinChange(false);
    setNewPin('');
    setConfirmPin('');
  };

  // Handle kitab upload
  const handleKitabUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      toast.loading('Memuat naik kitab...');
      await axios.post(`${API}/admin/upload-kitab`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setKitabFile(file);
      setKitabUploaded(true);
      toast.dismiss();
      toast.success('Kitab berjaya dimuat naik!');
    } catch (error) {
      toast.dismiss();
      toast.error('Gagal memuat naik kitab');
    }
  };

  // Handle AI query
  const handleAiQuery = async () => {
    if (!aiQuery.trim()) {
      toast.error('Sila masukkan soalan');
      return;
    }

    setAiLoading(true);
    setAiResponse(null);

    try {
      const response = await axios.post(`${API}/admin/ai-suggestion`, {
        query: aiQuery,
        context: 'marketing_sales_ops'
      }, { headers });
      
      setAiResponse(response.data);
    } catch (error) {
      toast.error('Gagal mendapat cadangan AI');
    } finally {
      setAiLoading(false);
    }
  };

  // Save system settings (including KPI config to localStorage for Channel Performance)
  const handleSaveSettings = async () => {
    try {
      // Save KPI config to localStorage (used by Channel Performance page)
      const kpiConfig = {
        healthScoreWeights,
        conversionThresholds,
        formulas: formulas.filter(f => f.enabled),
        customFormulas: customFormulas.filter(f => f.enabled && f.name),
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem('kpiConfig', JSON.stringify(kpiConfig));
      
      // Also save to backend if needed
      await axios.put(`${API}/admin/settings`, { ...systemSettings, kpiConfig }, { headers });
      toast.success('Tetapan KPI berjaya disimpan! Skor Health Score kini akan dikira berdasarkan konfigurasi ini.');
    } catch (error) {
      // Even if backend fails, localStorage save succeeded
      toast.success('Tetapan KPI disimpan ke localStorage');
      console.error('Backend save failed:', error);
    }
  };
  
  // Load KPI config from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('kpiConfig');
      if (stored) {
        const config = JSON.parse(stored);
        if (config.healthScoreWeights) setHealthScoreWeights(config.healthScoreWeights);
        if (config.conversionThresholds) setConversionThresholds(config.conversionThresholds);
        // Note: formulas are loaded with defaults, can be updated from stored if needed
      }
    } catch (e) {
      console.warn('Failed to load stored KPI config:', e);
    }
  }, []);

  // User management
  const handleUpdateUser = async (userId, updates) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, updates, { headers });
      toast.success('User dikemaskini');
      fetchAllData();
    } catch (error) {
      toast.error('Gagal kemaskini user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Padam user ini?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      toast.success('User dipadam');
      fetchAllData();
    } catch (error) {
      toast.error('Gagal padam user');
    }
  };

  const handleResetPassword = async (userId, userName) => {
    if (!window.confirm(`Reset password untuk ${userName}? Password akan ditukar ke 'underads123' dan user perlu tukar semasa login.`)) return;
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/reset-password`, {}, { headers });
      toast.success(`Password ${userName} telah di-reset ke: ${res.data.temp_password}`, { duration: 10000 });
    } catch (error) {
      toast.error('Gagal reset password');
    }
  };


  // Add new user
  const handleAddUser = async () => {
    if (!newUserForm.name || !newUserForm.email || !newUserForm.password) {
      toast.error('Sila isi semua maklumat');
      return;
    }
    try {
      await axios.post(`${API}/admin/users`, newUserForm, { headers });
      toast.success('User berjaya ditambah! Password: ' + newUserForm.password);
      setShowAddUserModal(false);
      setNewUserForm({ name: '', email: '', password: '', role_id: 'member' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal tambah user');
    }
  };

  // Delete role
  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Padam role ini? Pastikan tiada user yang menggunakan role ini.')) return;
    try {
      await axios.delete(`${API}/admin/roles/${roleId}`, { headers });
      toast.success('Role dipadam');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal padam role');
    }
  };

  // Integration management
  const handleUpdateIntegration = async (ibId, updates) => {
    try {
      await axios.put(`${API}/admin/integrations/${ibId}`, updates, { headers });
      toast.success('Integration dikemaskini');
      setShowIntegrationModal(false);
      fetchAllData();
    } catch (error) {
      toast.error('Gagal kemaskini integration');
    }
  };

  const handleTestIntegration = async (ibId, type) => {
    try {
      await axios.post(`${API}/admin/integrations/${ibId}/test?integration_type=${type}`, {}, { headers });
      toast.success(`Test ${type} berjaya!`);
    } catch (error) {
      toast.error(`Test ${type} gagal`);
    }
  };

  // IB management
  const handleCreateIB = async (ibData) => {
    try {
      await axios.post(`${API}/admin/ib`, ibData, { headers });
      toast.success('IB berjaya dicipta');
      setShowIBModal(false);
      fetchAllData();
    } catch (error) {
      toast.error('Gagal cipta IB');
    }
  };

  const handleDeleteIB = async (ibId) => {
    if (!window.confirm('Padam IB ini?')) return;
    try {
      await axios.delete(`${API}/admin/ib/${ibId}`, { headers });
      toast.success('IB dipadam');
      fetchAllData();
    } catch (error) {
      toast.error('Gagal padam IB');
    }
  };

  // Export data
  const handleExportData = async () => {
    try {
      const response = await axios.get(`${API}/admin/export/all`, { headers });
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `underads_export_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      toast.success('Data dieksport');
    } catch (error) {
      toast.error('Gagal eksport data');
    }
  };

  // Role management
  const handleSaveRole = async (roleData) => {
    try {
      if (selectedRole?.id) {
        await axios.put(`${API}/admin/roles/${selectedRole.id}`, roleData, { headers });
      } else {
        await axios.post(`${API}/admin/roles`, roleData, { headers });
      }
      toast.success('Role disimpan');
      setShowRoleModal(false);
      fetchAllData();
    } catch (error) {
      toast.error('Gagal simpan role');
    }
  };

  return (
    <ResponsiveLayout>
    <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))]">
      
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-[#09090B]/95 backdrop-blur-md border-b border-[hsl(var(--border))]">
          <div className="px-8 py-5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#E63946] via-[#DC2626] to-[#B91C1C] flex items-center justify-center shadow-lg shadow-[#E63946]/30">
                <Shield size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold font-heading tracking-tight" data-testid="admin-page-title">Admin Panel</h1>
                <p className="text-sm text-[hsl(var(--text-muted))]">Kawalan Sistem • Developer Access</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Badge className="bg-[#E63946]/20 text-[#E63946] border border-[#E63946]/30">
                <Lock size={12} className="mr-1" />
                Restricted Access
              </Badge>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowPinChange(true)}
                className="border-[hsl(var(--border))] text-white hover:bg-[#E63946]/10"
              >
                <Key size={14} className="mr-2" />
                Tukar PIN
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchAllData}
                className="border-[hsl(var(--border))] text-white hover:bg-[#3B82F6]/10"
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="px-8 py-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <TabsList className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] p-1 rounded-xl overflow-x-auto flex-nowrap whitespace-nowrap gap-1 max-w-full">
                <TabsTrigger value="dashboard" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0">
                  <BarChart3 size={14} className="mr-1.5" />Dashboard
                </TabsTrigger>
                <TabsTrigger value="mood-analytics" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0">
                  <Heart size={14} className="mr-1.5" />Mood
                </TabsTrigger>
                <TabsTrigger value="kpi-metrics" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0">
                  <Zap size={14} className="mr-1.5" />KPI Settings
                </TabsTrigger>
                <TabsTrigger value="leaderboard" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0">
                  <Trophy size={14} className="mr-1.5" />Leaderboard
                </TabsTrigger>
                <TabsTrigger value="broker-cleanup" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0">
                  <Database size={14} className="mr-1.5" />Broker Cleanup
                </TabsTrigger>
                <TabsTrigger value="data-import" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0">
                  <Upload size={14} className="mr-1.5" />Data Import
                </TabsTrigger>
                <TabsTrigger value="exchange-rates" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0">
                  <DollarSign size={14} className="mr-1.5" />Rates
                </TabsTrigger>
                <TabsTrigger value="users" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0">
                  <Users size={14} className="mr-1.5" />Users
                </TabsTrigger>
                <TabsTrigger value="integrations" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0">
                  <Link2 size={14} className="mr-1.5" />Integrations
                </TabsTrigger>
                <TabsTrigger value="activity" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0">
                  <Activity size={14} className="mr-1.5" />Activity
                </TabsTrigger>
                <TabsTrigger value="security" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0">
                  <Shield size={14} className="mr-1.5" />Security
                </TabsTrigger>
                <TabsTrigger value="notifications" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0">
                  <Bell size={14} className="mr-1.5" />Notifs
                </TabsTrigger>
                <TabsTrigger value="data" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0">
                  <Download size={14} className="mr-1.5" />Data
                </TabsTrigger>
                <TabsTrigger value="ai-brain" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0">
                  <Brain size={14} className="mr-1.5" />AI
                </TabsTrigger>
                <TabsTrigger value="reports" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0">
                  <BarChart3 size={14} className="mr-1.5" />Reports
                </TabsTrigger>
                <TabsTrigger value="tg-reports" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0">
                  <Send size={14} className="mr-1.5" />TG Reports
                </TabsTrigger>
                <TabsTrigger value="onboarding" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0">
                  <Rocket size={14} className="mr-1.5" />Onboarding
                </TabsTrigger>
                <TabsTrigger value="scorecard" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0" data-testid="tab-scorecard">
                  <Target size={14} className="mr-1.5" />Scorecard
                </TabsTrigger>
                <TabsTrigger value="data-quality" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0" data-testid="tab-data-quality">
                  <Shield size={14} className="mr-1.5" />Data Quality
                </TabsTrigger>
                <TabsTrigger value="recovery" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0" data-testid="tab-recovery">
                  <Sparkles size={14} className="mr-1.5" />Recovery
                </TabsTrigger>
                <TabsTrigger value="backup" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white rounded-lg text-xs flex-shrink-0" data-testid="tab-backup">
                  <Shield size={14} className="mr-1.5" />Backup
                </TabsTrigger>
              </TabsList>

              {/* ===== DASHBOARD SETTINGS TAB ===== */}
              <TabsContent value="dashboard" className="space-y-6">
                {/* Stats Overview */}
                <div className="grid grid-cols-6 gap-4">
                  {[
                    { label: 'Users', value: dbStats.users_count || 0, icon: Users, color: '#3B82F6' },
                    { label: 'IBs', value: dbStats.ibs_count || 0, icon: Database, color: '#10B981' },
                    { label: 'Channels', value: dbStats.channels_count || 0, icon: Hash, color: '#8B5CF6' },
                    { label: 'Daily Data', value: dbStats.daily_data_count || 0, icon: BarChart3, color: '#F59E0B' },
                    { label: 'Logs', value: dbStats.activity_logs_count || 0, icon: Activity, color: '#EC4899' },
                    { label: 'Integrations', value: dbStats.integrations_count || 0, icon: Link2, color: '#06B6D4' },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${stat.color}20` }}>
                          <stat.icon size={18} style={{ color: stat.color }} />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-white">{stat.value.toLocaleString()}</p>
                          <p className="text-xs text-[#6B7280]">{stat.label}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* System Settings */}
                <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                  <SectionHeader icon={Settings} title="System Settings" subtitle="Global configuration" />
                  <div className="p-6 grid grid-cols-3 gap-6">
                    {/* General Settings */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                        <Globe size={14} className="text-[#3B82F6]" /> General
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-[#6B7280] text-xs">Currency</Label>
                          <Select value={systemSettings.currency || 'RM'} onValueChange={(v) => setSystemSettings({...systemSettings, currency: v})}>
                            <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="RM">RM (Ringgit)</SelectItem>
                              <SelectItem value="USD">USD (Dollar)</SelectItem>
                              <SelectItem value="SGD">SGD (Singapore)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[#6B7280] text-xs">Date Format</Label>
                          <Select value={systemSettings.date_format || 'DD/MM/YYYY'} onValueChange={(v) => setSystemSettings({...systemSettings, date_format: v})}>
                            <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                              <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                              <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* KPI Thresholds */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                        <BarChart3 size={14} className="text-[#10B981]" /> KPI Thresholds
                      </h4>
                      <div className="space-y-3">
                        {[
                          { key: 'kpi_poor_threshold', label: 'Poor (Red) <', suffix: '%' },
                          { key: 'kpi_average_threshold', label: 'Average (Yellow) <', suffix: '%' },
                          { key: 'kpi_good_threshold', label: 'Good (Green) ≥', suffix: '%' },
                        ].map((item) => (
                          <div key={item.key} className="flex items-center justify-between">
                            <Label className="text-[#6B7280] text-xs">{item.label}</Label>
                            <div className="flex items-center gap-1">
                              <Input 
                                type="number" 
                                value={systemSettings[item.key] || 0}
                                onChange={(e) => setSystemSettings({...systemSettings, [item.key]: parseInt(e.target.value)})}
                                className="w-16 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white text-center text-sm h-8"
                              />
                              <span className="text-[#6B7280] text-xs">{item.suffix}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* ROI Thresholds */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                        <Zap size={14} className="text-[#F59E0B]" /> ROI Tiers
                      </h4>
                      <div className="space-y-3">
                        {[
                          { key: 'roi_bad_threshold', label: 'Bad ROI <', suffix: 'x' },
                          { key: 'roi_average_threshold', label: 'Average ROI <', suffix: 'x' },
                          { key: 'roi_good_threshold', label: 'Good ROI ≥', suffix: 'x' },
                        ].map((item) => (
                          <div key={item.key} className="flex items-center justify-between">
                            <Label className="text-[#6B7280] text-xs">{item.label}</Label>
                            <div className="flex items-center gap-1">
                              <Input 
                                type="number" 
                                step="0.1"
                                value={systemSettings[item.key] || 0}
                                onChange={(e) => setSystemSettings({...systemSettings, [item.key]: parseFloat(e.target.value)})}
                                className="w-16 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white text-center text-sm h-8"
                              />
                              <span className="text-[#6B7280] text-xs">{item.suffix}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="px-6 pb-6">
                    <Button onClick={handleSaveSettings} className="bg-gradient-to-r from-[#E63946] to-[#DC2626] text-white">
                      <Save size={14} className="mr-2" />
                      Simpan Tetapan
                    </Button>
                  </div>
                </div>
              </TabsContent>

              {/* ===== MOOD ANALYTICS TAB ===== */}
              <TabsContent value="mood-analytics" className="space-y-6">
                <MoodAnalyticsSection />
              </TabsContent>

              {/* ===== KPI METRICS BY DEPARTMENT TAB ===== */}
              <TabsContent value="kpi-metrics" className="space-y-6">
                
                {/* ========== 1. HEALTH SCORE CONFIGURATION ========== */}
                <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                  <button 
                    onClick={() => setExpandedSection(expandedSection === 'healthScore' ? '' : 'healthScore')}
                    className="w-full"
                  >
                    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#3B82F6]/20 via-[#8B5CF6]/15 to-transparent relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-[#3B82F6]/10 to-transparent opacity-50" />
                      <div className="relative flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#3B82F6]/30 to-[#8B5CF6]/20 border border-[#3B82F6]/40 shadow-[0_0_15px_-3px_#3B82F6]">
                            <Gauge size={20} className="text-white" />
                          </div>
                          <div className="text-left">
                            <h3 className="text-lg font-bold text-white font-heading">Health Score Configuration</h3>
                            <p className="text-xs text-[hsl(var(--text-muted))]">Configure weights for department health score calculation</p>
                          </div>
                        </div>
                        {expandedSection === 'healthScore' ? <ChevronUp size={20} className="text-[#9CA3AF]" /> : <ChevronDown size={20} className="text-[#9CA3AF]" />}
                      </div>
                    </div>
                  </button>
                  
                  {expandedSection === 'healthScore' && (
                    <div className="p-6 space-y-6">
                      {/* Formula Explanation */}
                      <div className="p-4 bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-lg">
                        <p className="text-sm text-white font-medium mb-2">Health Score Formula</p>
                        <code className="text-xs text-[#9CA3AF] block">
                          Health Score = (Target Achievement × W1) + (Funnel Score × W2) + (Rates Score × W3) + (Efficiency Score × W4)
                        </code>
                        <p className="text-xs text-[#6B7280] mt-2">Adjust weights below. Total must equal 100%.</p>
                      </div>
                      
                      {/* Department Weights */}
                      {[
                        { key: 'marketing', label: 'Marketing', color: '#E63946', components: [
                          { key: 'targetAchievement', label: 'Target Achievement', desc: '% of targets met' },
                          { key: 'conversionFunnel', label: 'Conversion Funnel Score', desc: 'Average funnel stage performance' },
                          { key: 'conversionRates', label: 'Conversion Rates Score', desc: 'Stage-to-stage conversion quality' },
                          { key: 'adsEfficiency', label: 'Ads Efficiency Score', desc: 'CPM, CTR, CPC vs benchmarks' },
                        ]},
                        { key: 'sales', label: 'Sales', color: '#10B981', components: [
                          { key: 'targetAchievement', label: 'Target Achievement', desc: '% of sales targets met' },
                          { key: 'pipeline', label: 'Pipeline Score', desc: 'Lead pipeline health' },
                          { key: 'closingRate', label: 'Closing Rate Score', desc: 'Conversion to closed deals' },
                          { key: 'commission', label: 'Commission Score', desc: 'Revenue generation' },
                        ]},
                        { key: 'operations', label: 'Operations', color: '#F59E0B', components: [
                          { key: 'targetAchievement', label: 'Target Achievement', desc: '% of ops targets met' },
                          { key: 'engagement', label: 'Engagement Score', desc: 'User engagement metrics' },
                          { key: 'activity', label: 'Activity Score', desc: 'Live sessions, signals, content' },
                          { key: 'retention', label: 'Retention Score', desc: 'Client retention rate' },
                        ]},
                      ].map((dept) => {
                        const weights = healthScoreWeights[dept.key];
                        const total = Object.values(weights).reduce((a, b) => a + b, 0);
                        const isValid = total === 100;
                        
                        return (
                          <div key={dept.key} className="p-4 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: dept.color }} />
                                <h4 className="font-semibold text-white">{dept.label} Department</h4>
                              </div>
                              <Badge className={`${isValid ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#E63946]/20 text-[#E63946]'} border-none`}>
                                Total: {total}% {isValid ? '✓' : '(must be 100%)'}
                              </Badge>
                            </div>
                            
                            <div className="grid grid-cols-4 gap-4">
                              {dept.components.map((comp) => (
                                <div key={comp.key} className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <Label className="text-xs text-[#9CA3AF]">{comp.label}</Label>
                                    <span className="text-sm font-mono font-bold" style={{ color: dept.color }}>{weights[comp.key]}%</span>
                                  </div>
                                  <Slider
                                    value={[weights[comp.key]]}
                                    onValueChange={(val) => setHealthScoreWeights({
                                      ...healthScoreWeights,
                                      [dept.key]: { ...weights, [comp.key]: val[0] }
                                    })}
                                    max={100}
                                    step={5}
                                    className="w-full"
                                  />
                                  <p className="text-[10px] text-[#6B7280]">{comp.desc}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Score Display Legend */}
                      <div className="flex items-center gap-4 p-3 bg-[hsl(var(--surface-elevated))] rounded-lg">
                        <span className="text-xs text-[#6B7280]">Score Display:</span>
                        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-[#E63946]" /><span className="text-xs text-[#9CA3AF]">0-49 Poor</span></div>
                        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-[#F59E0B]" /><span className="text-xs text-[#9CA3AF]">50-69 Average</span></div>
                        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-[#10B981]" /><span className="text-xs text-[#9CA3AF]">70-89 Good</span></div>
                        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-[#3B82F6]" /><span className="text-xs text-[#9CA3AF]">90-100 Excellent</span></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ========== 2. CONVERSION THRESHOLDS ========== */}
                <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                  <button 
                    onClick={() => setExpandedSection(expandedSection === 'conversionThresholds' ? '' : 'conversionThresholds')}
                    className="w-full"
                  >
                    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#10B981]/20 via-[#F59E0B]/15 to-transparent relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-[#10B981]/10 to-transparent opacity-50" />
                      <div className="relative flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#10B981]/30 to-[#F59E0B]/20 border border-[#10B981]/40 shadow-[0_0_15px_-3px_#10B981]">
                            <Percent size={20} className="text-white" />
                          </div>
                          <div className="text-left">
                            <h3 className="text-lg font-bold text-white font-heading">Conversion Thresholds</h3>
                            <p className="text-xs text-[hsl(var(--text-muted))]">Set Poor/Average/Good/Excellent ranges for each conversion stage</p>
                          </div>
                        </div>
                        {expandedSection === 'conversionThresholds' ? <ChevronUp size={20} className="text-[#9CA3AF]" /> : <ChevronDown size={20} className="text-[#9CA3AF]" />}
                      </div>
                    </div>
                  </button>
                  
                  {expandedSection === 'conversionThresholds' && (
                    <div className="p-6 space-y-4">
                      {/* Conversion Stages */}
                      {[
                        { key: 'ctaConversion', label: 'CTA Conversion', desc: 'LPV → CTA Clicks' },
                        { key: 'subsConversion', label: 'Subscriber Conversion', desc: 'CTA Clicks → Subscribers' },
                        { key: 'leadsConversion', label: 'Leads Conversion', desc: 'Subscribers → Leads' },
                        { key: 'closingRate', label: 'Closing Rate', desc: 'Leads → Closed' },
                        { key: 'depositRate', label: 'Deposit Rate', desc: 'Closed → Deposit' },
                      ].map((stage) => {
                        const thresholds = conversionThresholds[stage.key];
                        
                        return (
                          <div key={stage.key} className="p-4 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl">
                            <div className="flex items-center gap-3 mb-4">
                              <Target size={16} className="text-[#10B981]" />
                              <div>
                                <p className="font-medium text-white">{stage.label}</p>
                                <p className="text-xs text-[#6B7280]">{stage.desc}</p>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-4 gap-4">
                              {['poor', 'average', 'good', 'excellent'].map((tier) => {
                                const colors = {
                                  poor: '#E63946',
                                  average: '#F59E0B',
                                  good: '#10B981',
                                  excellent: '#3B82F6',
                                };
                                
                                return (
                                  <div key={tier} className="text-center">
                                    <Label className="text-xs capitalize mb-2 block" style={{ color: colors[tier] }}>{tier}</Label>
                                    <div className="flex items-center gap-1">
                                      <span className="text-xs text-[#6B7280]">{tier === 'poor' ? '<' : tier === 'excellent' ? '>' : ''}</span>
                                      <Input
                                        type="number"
                                        value={thresholds[tier]}
                                        onChange={(e) => setConversionThresholds({
                                          ...conversionThresholds,
                                          [stage.key]: { ...thresholds, [tier]: parseFloat(e.target.value) || 0 }
                                        })}
                                        className="h-8 text-center bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white"
                                      />
                                      <span className="text-xs text-[#6B7280]">%</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* ========== 3. FORMULA BUILDER ========== */}
                <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                  <button 
                    onClick={() => setExpandedSection(expandedSection === 'formulaBuilder' ? '' : 'formulaBuilder')}
                    className="w-full"
                  >
                    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
                      <div className="relative flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
                            <Calculator size={20} className="text-white" />
                          </div>
                          <div className="text-left">
                            <h3 className="text-lg font-bold text-white font-heading">Formula Builder</h3>
                            <p className="text-xs text-[hsl(var(--text-muted))]">Predefined formulas with custom slots for advanced metrics</p>
                          </div>
                        </div>
                        {expandedSection === 'formulaBuilder' ? <ChevronUp size={20} className="text-[#9CA3AF]" /> : <ChevronDown size={20} className="text-[#9CA3AF]" />}
                      </div>
                    </div>
                  </button>
                  
                  {expandedSection === 'formulaBuilder' && (
                    <div className="p-6 space-y-6">
                      {/* Predefined Formulas */}
                      <div>
                        <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                          <Zap size={14} className="text-[#F59E0B]" /> Predefined Formulas
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          {formulas.map((formula) => (
                            <div 
                              key={formula.id} 
                              className={`p-4 rounded-xl border transition-all ${formula.enabled ? 'bg-[hsl(var(--surface-elevated))] border-[#E63946]/30' : 'bg-[hsl(var(--surface))] border-[hsl(var(--border))] opacity-60'}`}
                            >
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Switch 
                                    checked={formula.enabled}
                                    onCheckedChange={(checked) => {
                                      setFormulas(formulas.map(f => f.id === formula.id ? {...f, enabled: checked} : f));
                                    }}
                                  />
                                  <span className="font-bold text-white">{formula.name}</span>
                                </div>
                                <Badge className="bg-[#E63946]/20 text-[#E63946] border-none text-[10px]">Predefined</Badge>
                              </div>
                              
                              <div className="p-2 bg-[hsl(var(--surface))] rounded-lg mb-3">
                                <code className="text-xs text-[#9CA3AF]">{formula.formula}</code>
                              </div>
                              
                              <div className="grid grid-cols-4 gap-2">
                                {['poor', 'average', 'good', 'excellent'].map((tier) => {
                                  const colors = { poor: '#E63946', average: '#F59E0B', good: '#10B981', excellent: '#3B82F6' };
                                  return (
                                    <div key={tier} className="text-center">
                                      <p className="text-[8px] uppercase mb-1" style={{ color: colors[tier] }}>{tier}</p>
                                      <Input
                                        type="number"
                                        step="0.1"
                                        value={formula.benchmarks[tier]}
                                        onChange={(e) => {
                                          setFormulas(formulas.map(f => f.id === formula.id 
                                            ? {...f, benchmarks: {...f.benchmarks, [tier]: parseFloat(e.target.value) || 0}}
                                            : f
                                          ));
                                        }}
                                        disabled={!formula.enabled}
                                        className="h-6 text-[10px] bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-center p-1"
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Custom Formula Slots */}
                      <div>
                        <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                          <Plus size={14} className="text-[#3B82F6]" /> Custom Formula Slots
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          {customFormulas.map((formula, idx) => (
                            <div 
                              key={formula.id} 
                              className={`p-4 rounded-xl border ${formula.enabled ? 'bg-[hsl(var(--surface-elevated))] border-[#3B82F6]/30' : 'bg-[hsl(var(--surface))] border-dashed border-[hsl(var(--border))]'}`}
                            >
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Switch 
                                    checked={formula.enabled}
                                    onCheckedChange={(checked) => {
                                      setCustomFormulas(customFormulas.map((f, i) => i === idx ? {...f, enabled: checked} : f));
                                    }}
                                  />
                                  <Input
                                    placeholder="Formula Name"
                                    value={formula.name}
                                    onChange={(e) => {
                                      setCustomFormulas(customFormulas.map((f, i) => i === idx ? {...f, name: e.target.value} : f));
                                    }}
                                    className="h-7 w-32 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white"
                                  />
                                </div>
                                <Badge className="bg-[#3B82F6]/20 text-[#3B82F6] border-none text-[10px]">Custom {idx + 1}</Badge>
                              </div>
                              
                              <div className="mb-3">
                                <Label className="text-[10px] text-[#6B7280] mb-1 block">Formula Expression</Label>
                                <Input
                                  placeholder="e.g., Ad Spent ÷ Active Client"
                                  value={formula.formula}
                                  onChange={(e) => {
                                    setCustomFormulas(customFormulas.map((f, i) => i === idx ? {...f, formula: e.target.value} : f));
                                  }}
                                  className="text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white"
                                />
                              </div>
                              
                              <div className="mb-3">
                                <Label className="text-[10px] text-[#6B7280] mb-1 block">Available Variables</Label>
                                <div className="flex flex-wrap gap-1">
                                  {['Ad Spent', 'Impressions', 'Clicks', 'LPV', 'CTA Clicks', 'Subscribers', 'Leads', 'Closing', 'Deposit', 'Commission', 'Active Client', 'Traded Lot'].map((v) => (
                                    <Badge key={v} className="bg-[hsl(var(--surface))] text-[#9CA3AF] border-[hsl(var(--border))] text-[8px] cursor-pointer hover:bg-[#3B82F6]/20 hover:text-[#3B82F6]">
                                      {v}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                              
                              {formula.enabled && (
                                <div className="grid grid-cols-4 gap-2">
                                  {['poor', 'average', 'good', 'excellent'].map((tier) => {
                                    const colors = { poor: '#E63946', average: '#F59E0B', good: '#10B981', excellent: '#3B82F6' };
                                    return (
                                      <div key={tier} className="text-center">
                                        <p className="text-[8px] uppercase mb-1" style={{ color: colors[tier] }}>{tier}</p>
                                        <Input
                                          type="number"
                                          step="0.1"
                                          value={formula.benchmarks[tier]}
                                          onChange={(e) => {
                                            setCustomFormulas(customFormulas.map((f, i) => i === idx 
                                              ? {...f, benchmarks: {...f.benchmarks, [tier]: parseFloat(e.target.value) || 0}}
                                              : f
                                            ));
                                          }}
                                          className="h-6 text-[10px] bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-center p-1"
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ========== 4. DEPARTMENT KPI THRESHOLDS (Original - Collapsed) ========== */}
                <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                  <button 
                    onClick={() => setExpandedSection(expandedSection === 'departmentKPI' ? '' : 'departmentKPI')}
                    className="w-full"
                  >
                    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#8B5CF6]/20 via-[#EC4899]/15 to-transparent relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-[#8B5CF6]/10 to-transparent opacity-50" />
                      <div className="relative flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#8B5CF6]/30 to-[#EC4899]/20 border border-[#8B5CF6]/40 shadow-[0_0_15px_-3px_#8B5CF6]">
                            <BarChart3 size={20} className="text-white" />
                          </div>
                          <div className="text-left">
                            <h3 className="text-lg font-bold text-white font-heading">Department KPI Thresholds</h3>
                            <p className="text-xs text-[hsl(var(--text-muted))]">Set performance ranges for each department metric</p>
                          </div>
                        </div>
                        {expandedSection === 'departmentKPI' ? <ChevronUp size={20} className="text-[#9CA3AF]" /> : <ChevronDown size={20} className="text-[#9CA3AF]" />}
                      </div>
                    </div>
                  </button>
                  
                  {expandedSection === 'departmentKPI' && (
                    <div className="p-6 space-y-6">
                      {['Marketing', 'Sales', 'Operation'].map((dept) => (
                        <div key={dept} className="p-4 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl">
                          <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                            {dept === 'Marketing' ? <BarChart3 size={16} className="text-[#E63946]" /> : dept === 'Sales' ? <Zap size={16} className="text-[#10B981]" /> : <Settings size={16} className="text-[#F59E0B]" />}
                            {dept} KPI Metrics
                          </h4>
                          <div className="grid grid-cols-4 gap-3">
                            {dept === 'Marketing' && [
                              { label: 'Spend', key: `${dept.toLowerCase()}_spend` },
                              { label: 'Impressions', key: `${dept.toLowerCase()}_impressions` },
                              { label: 'Clicks', key: `${dept.toLowerCase()}_clicks` },
                              { label: 'Subscribers', key: `${dept.toLowerCase()}_subscribers` },
                              { label: 'Leads', key: `${dept.toLowerCase()}_leads` },
                              { label: 'CPL', key: `${dept.toLowerCase()}_cpl` },
                              { label: 'CTR', key: `${dept.toLowerCase()}_ctr` },
                              { label: 'Conversion', key: `${dept.toLowerCase()}_conversion` },
                            ].map((metric) => (
                              <div key={metric.key} className="p-2 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg">
                                <p className="text-[10px] text-[#9CA3AF] mb-2">{metric.label}</p>
                                <div className="grid grid-cols-4 gap-1">
                                  {['poor', 'average', 'good', 'excellent'].map((tier) => (
                                    <Input
                                      key={tier}
                                      type="number"
                                      placeholder="0"
                                      title={tier}
                                      className="h-5 text-[9px] bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white text-center p-0"
                                      defaultValue={systemSettings[`${metric.key}_${tier}`] || ''}
                                      onChange={(e) => setSystemSettings({...systemSettings, [`${metric.key}_${tier}`]: e.target.value})}
                                    />
                                  ))}
                                </div>
                              </div>
                            ))}
                            {dept === 'Sales' && [
                              { label: 'Commission', key: `${dept.toLowerCase()}_commission` },
                              { label: 'Deposit', key: `${dept.toLowerCase()}_deposit` },
                              { label: 'Traded Lot', key: `${dept.toLowerCase()}_lot` },
                              { label: 'Active Client', key: `${dept.toLowerCase()}_client` },
                              { label: 'New Client', key: `${dept.toLowerCase()}_new_client` },
                              { label: 'Closed', key: `${dept.toLowerCase()}_closed` },
                              { label: 'ROI', key: `${dept.toLowerCase()}_roi` },
                              { label: 'Deal Size', key: `${dept.toLowerCase()}_deal_size` },
                            ].map((metric) => (
                              <div key={metric.key} className="p-2 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg">
                                <p className="text-[10px] text-[#9CA3AF] mb-2">{metric.label}</p>
                                <div className="grid grid-cols-4 gap-1">
                                  {['poor', 'average', 'good', 'excellent'].map((tier) => (
                                    <Input
                                      key={tier}
                                      type="number"
                                      placeholder="0"
                                      title={tier}
                                      className="h-5 text-[9px] bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white text-center p-0"
                                      defaultValue={systemSettings[`${metric.key}_${tier}`] || ''}
                                      onChange={(e) => setSystemSettings({...systemSettings, [`${metric.key}_${tier}`]: e.target.value})}
                                    />
                                  ))}
                                </div>
                              </div>
                            ))}
                            {dept === 'Operation' && [
                              { label: 'Response Time', key: `${dept.toLowerCase()}_response` },
                              { label: 'Tickets', key: `${dept.toLowerCase()}_tickets` },
                              { label: 'Rating', key: `${dept.toLowerCase()}_rating` },
                              { label: 'SLA', key: `${dept.toLowerCase()}_sla` },
                              { label: 'Escalation', key: `${dept.toLowerCase()}_escalation` },
                              { label: 'Resolution', key: `${dept.toLowerCase()}_resolution` },
                              { label: 'Issues', key: `${dept.toLowerCase()}_issues` },
                              { label: 'Tasks', key: `${dept.toLowerCase()}_tasks` },
                            ].map((metric) => (
                              <div key={metric.key} className="p-2 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg">
                                <p className="text-[10px] text-[#9CA3AF] mb-2">{metric.label}</p>
                                <div className="grid grid-cols-4 gap-1">
                                  {['poor', 'average', 'good', 'excellent'].map((tier) => (
                                    <Input
                                      key={tier}
                                      type="number"
                                      placeholder="0"
                                      title={tier}
                                      className="h-5 text-[9px] bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white text-center p-0"
                                      defaultValue={systemSettings[`${metric.key}_${tier}`] || ''}
                                      onChange={(e) => setSystemSettings({...systemSettings, [`${metric.key}_${tier}`]: e.target.value})}
                                    />
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Save Button */}
                <div className="flex gap-3">
                  <Button onClick={handleSaveSettings} className="bg-gradient-to-r from-[#E63946] to-[#DC2626] text-white flex-1">
                    <Save size={14} className="mr-2" />
                    Simpan Semua KPI Settings
                  </Button>
                  <Button variant="outline" className="border-[hsl(var(--border))] text-white">
                    <RefreshCw size={14} className="mr-2" />
                    Reset to Defaults
                  </Button>
                </div>

                {/* ========== 2. KPI PERFORMANCE THRESHOLDS ========== */}
                <KpiThresholdsTab />

              </TabsContent>

              {/* ===== EXCHANGE RATES TAB ===== */}
              <TabsContent value="exchange-rates" className="space-y-6">
                <AdminExchangeRatesTab />
              </TabsContent>

              {/* ===== LEADERBOARD SCORING TAB ===== */}
              <TabsContent value="leaderboard" className="space-y-6">
                <AdminLeaderboardTab />
              </TabsContent>

              {/* ===== BROKER CLEANUP TAB ===== */}
              <TabsContent value="broker-cleanup" className="space-y-6">
                <AdminBrokerCleanupTab />
              </TabsContent>

              {/* ===== DAILY DATA IMPORT TAB ===== */}
              <TabsContent value="data-import" className="space-y-6">
                <AdminDailyDataImportTab />
              </TabsContent>

              {/* ===== USERS & ROLES TAB ===== */}
              <TabsContent value="users" className="space-y-6">
                {/* Users List */}
                <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                  <SectionHeader 
                    icon={Users} 
                    title="Registered Users" 
                    subtitle={`${users.length} users registered`}
                    action={
                      <Button size="sm" onClick={() => setShowAddUserModal(true)} className="bg-[#E63946] text-white">
                        <UserPlus size={14} className="mr-1" /> Add User
                      </Button>
                    }
                  />
                  <div className="p-4">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-xs text-[#6B7280] border-b border-[hsl(var(--border))]">
                            <th className="pb-3 pl-3">User</th>
                            <th className="pb-3">Email</th>
                            <th className="pb-3">Role</th>
                            <th className="pb-3">Status</th>
                            <th className="pb-3">Permissions</th>
                            <th className="pb-3">Last Login</th>
                            <th className="pb-3">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map((user) => (
                            <tr key={user.id} className="border-b border-[hsl(var(--border))]/50 hover:bg-[hsl(var(--surface-elevated))]">
                              <td className="py-3 pl-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E63946] to-[#DC2626] flex items-center justify-center text-xs font-bold">
                                    {user.name?.charAt(0) || 'U'}
                                  </div>
                                  <span className="text-sm text-white">{user.name}</span>
                                </div>
                              </td>
                              <td className="py-3 text-sm text-[#9CA3AF]">{user.email}</td>
                              <td className="py-3">
                                <Select 
                                  value={user.role_id || 'viewer'} 
                                  onValueChange={(v) => {
                                    const role = roles.find(r => r.id === v);
                                    handleUpdateUser(user.id, { role_id: v, role_name: role?.name || v });
                                  }}
                                >
                                  <SelectTrigger className="w-28 h-7 text-xs bg-transparent border-[hsl(var(--border))]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {roles.map((role) => (
                                      <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="py-3"><StatusBadge status={user.status || 'active'} /></td>
                              <td className="py-3">
                                <Button variant="ghost" size="sm" className="h-7 text-[10px] text-[hsl(var(--text-muted))] hover:text-white"
                                  onClick={() => { setPermEditUser(user); setShowPermModal(true); }}>
                                  <Settings size={12} className="mr-1" />
                                  {user.permissions?.channel_access === 'specific' ? `${(user.permissions?.allowed_channels || []).length} channel` : 'All access'}
                                </Button>
                              </td>
                              <td className="py-3 text-xs text-[#6B7280]">
                                {user.last_login ? new Date(user.last_login).toLocaleString('ms-MY') : 'Never'}
                              </td>
                              <td className="py-3">
                                <div className="flex gap-1">
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleUpdateUser(user.id, { status: user.status === 'active' ? 'inactive' : 'active' })}
                                    className="h-7 w-7 p-0"
                                  >
                                    {user.status === 'active' ? <XCircle size={14} className="text-[#EF4444]" /> : <CheckCircle2 size={14} className="text-[#10B981]" />}
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleResetPassword(user.id, user.name)} className="h-7 w-7 p-0" title="Reset Password">
                                    <Key size={14} className="text-[#F59E0B]" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(user.id)} className="h-7 w-7 p-0" title="Delete User">
                                    <Trash2 size={14} className="text-[#EF4444]" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Roles Management */}
                <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                  <SectionHeader 
                    icon={Shield} 
                    title="Roles & Permissions" 
                    subtitle="Manage edit permissions per role"
                    action={
                      <Button size="sm" onClick={() => { setSelectedRole(null); setShowRoleModal(true); }} className="bg-[#E63946] text-white">
                        <Plus size={14} className="mr-1" /> Add Role
                      </Button>
                    }
                  />
                  <div className="p-4 grid grid-cols-3 gap-4">
                    {roles.map((role) => (
                      <div key={role.id} className="p-4 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold text-white">{role.name}</h4>
                          {!['admin', 'manager', 'viewer'].includes(role.id) && (
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => { setSelectedRole(role); setShowRoleModal(true); }} className="h-6 w-6 p-0">
                                <Edit size={12} />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteRole(role.id)} className="h-6 w-6 p-0 text-red-400 hover:text-red-500">
                                <Trash2 size={12} />
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className="space-y-2 text-xs">
                          <div className="flex items-center justify-between">
                            <span className="text-[#6B7280]">Edit IB</span>
                            {role.can_edit_ib ? <Check size={14} className="text-[#10B981]" /> : <XCircle size={14} className="text-[#EF4444]" />}
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[#6B7280]">Edit Campaign</span>
                            {role.can_edit_campaign ? <Check size={14} className="text-[#10B981]" /> : <XCircle size={14} className="text-[#EF4444]" />}
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[#6B7280]">Edit Daily Data</span>
                            {role.can_edit_daily_data ? <Check size={14} className="text-[#10B981]" /> : <XCircle size={14} className="text-[#EF4444]" />}
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[#6B7280]">Edit Settings</span>
                            {role.can_edit_settings ? <Check size={14} className="text-[#10B981]" /> : <XCircle size={14} className="text-[#EF4444]" />}
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[#6B7280]">Admin Access</span>
                            {role.can_access_admin ? <Check size={14} className="text-[#10B981]" /> : <XCircle size={14} className="text-[#EF4444]" />}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* ===== CHANNEL INTEGRATIONS TAB ===== */}
              <TabsContent value="integrations" className="space-y-6">
                {/* Link Health Check — helps admin spot unlinked channels + orphaned configs */}
                <ChannelLinkHealthCard />

                <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                  <SectionHeader 
                    icon={Link2} 
                    title="Channel Integrations" 
                    subtitle="Manage integrations per IB/Channel"
                    action={
                      <Button size="sm" onClick={() => { setSelectedIB(null); setShowIBModal(true); }} className="bg-[#E63946] text-white">
                        <Plus size={14} className="mr-1" /> Add IB
                      </Button>
                    }
                  />
                  <div className="p-4">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-xs text-[#6B7280] border-b border-[hsl(var(--border))]">
                            <th className="pb-3 pl-3">IB/Channel</th>
                            <th className="pb-3">Status</th>
                            <th className="pb-3">Google Sheets</th>
                            <th className="pb-3">Telegram/tgstat</th>
                            <th className="pb-3">Broker API</th>
                            <th className="pb-3">Webhook</th>
                            <th className="pb-3">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {integrations.map((int) => {
                            const ibData = ibs.find(i => i.id === int.ib_id);
                            const isActive = ibData?.status === 'active';
                            return (
                            <tr key={int.ib_id} className="border-b border-[hsl(var(--border))]/50 hover:bg-[hsl(var(--surface-elevated))]">
                              <td className="py-3 pl-3">
                                <span className="text-sm font-medium text-white">{int.ib_name || ibData?.name || int.ib_id}</span>
                              </td>
                              <td className="py-3">
                                <button
                                  onClick={async () => {
                                    try {
                                      const newStatus = isActive ? 'inactive' : 'active';
                                      await axios.put(`${API}/ib/${int.ib_id}/status`, { status: newStatus }, { headers });
                                      setIbs(prev => prev.map(ib => ib.id === int.ib_id ? { ...ib, status: newStatus } : ib));
                                      toast.success(`${int.ib_name || ibData?.name} status updated to ${newStatus}`);
                                    } catch (error) {
                                      toast.error('Failed to update status');
                                    }
                                  }}
                                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                                    isActive 
                                      ? 'bg-[#10B981]/20 text-[#10B981] hover:bg-[#10B981]/30' 
                                      : 'bg-[#6B7280]/20 text-[#6B7280] hover:bg-[#6B7280]/30'
                                  }`}
                                >
                                  {isActive ? '● Active' : '○ Inactive'}
                                </button>
                              </td>
                              <td className="py-3"><StatusBadge status={int.gsheet_performance_status} /></td>
                              <td className="py-3"><StatusBadge status={int.telegram_status} /></td>
                              <td className="py-3"><StatusBadge status={int.broker_status} /></td>
                              <td className="py-3"><StatusBadge status={int.webhook_status} /></td>
                              <td className="py-3">
                                <div className="flex gap-1">
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => { setSelectedIntegration(int); setShowIntegrationModal(true); }}
                                    className="h-7 text-xs"
                                  >
                                    <Settings size={12} className="mr-1" /> Configure
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleDeleteIB(int.ib_id)}
                                    className="h-7 w-7 p-0 text-[#EF4444] hover:bg-[#EF4444]/10"
                                  >
                                    <Trash2 size={12} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          )})}
                          {integrations.length === 0 && (
                            <tr>
                              <td colSpan="7" className="py-8 text-center text-[#6B7280]">
                                <Database size={40} className="mx-auto mb-2 opacity-50" />
                                <p>Tiada IB. Klik &quot;Add IB&quot; untuk mula.</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* ===== ACTIVITY LOGS TAB ===== */}
              <TabsContent value="activity" className="space-y-6">
                <AdminActivityLogsTab />
              </TabsContent>

              <TabsContent value="security" className="space-y-6">
                <AdminSecurityTab />
              </TabsContent>

              {/* ===== NOTIFICATIONS TAB ===== */}
              <TabsContent value="notifications" className="space-y-6">
                <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                  <SectionHeader icon={Bell} title="Notification Settings" subtitle="Configure alerts and reports" />
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Bell size={16} className="text-[#3B82F6]" />
                            <span className="text-sm font-medium text-white">Email Notifications</span>
                          </div>
                          <Switch checked={systemSettings.email_notifications} onCheckedChange={(v) => setSystemSettings({...systemSettings, email_notifications: v})} />
                        </div>
                        <p className="text-xs text-[#6B7280]">Terima notifikasi melalui email</p>
                      </div>
                      <div className="p-4 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Calendar size={16} className="text-[#10B981]" />
                            <span className="text-sm font-medium text-white">Daily Report</span>
                          </div>
                          <Switch checked={systemSettings.daily_report} onCheckedChange={(v) => setSystemSettings({...systemSettings, daily_report: v})} />
                        </div>
                        <p className="text-xs text-[#6B7280]">Laporan harian automatik</p>
                      </div>
                      <div className="p-4 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <AlertTriangle size={16} className="text-[#F59E0B]" />
                            <span className="text-sm font-medium text-white">KPI Alerts</span>
                          </div>
                          <Switch checked={systemSettings.alert_enabled} onCheckedChange={(v) => setSystemSettings({...systemSettings, alert_enabled: v})} />
                        </div>
                        <p className="text-xs text-[#6B7280]">Amaran bila KPI di bawah threshold</p>
                      </div>
                      <div className="p-4 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Clock size={16} className="text-[#8B5CF6]" />
                            <span className="text-sm font-medium text-white">Weekly Report</span>
                          </div>
                          <Switch checked={systemSettings.weekly_report} onCheckedChange={(v) => setSystemSettings({...systemSettings, weekly_report: v})} />
                        </div>
                        <p className="text-xs text-[#6B7280]">Laporan mingguan automatik</p>
                      </div>
                    </div>
                    <Button onClick={handleSaveSettings} className="bg-gradient-to-r from-[#E63946] to-[#DC2626] text-white">
                      <Save size={14} className="mr-2" />
                      Simpan Notification Settings
                    </Button>
                  </div>
                </div>
              </TabsContent>

              {/* ===== DATA MANAGEMENT TAB ===== */}
              <TabsContent value="data" className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                    <SectionHeader icon={Download} title="Export Data" subtitle="Download all data" />
                    <div className="p-6">
                      <p className="text-sm text-[#9CA3AF] mb-4">Export semua data dalam format JSON termasuk IBs, Channels, Daily Data, Users, dan Settings.</p>
                      <Button onClick={handleExportData} className="bg-gradient-to-r from-[#3B82F6] to-[#2563EB] text-white">
                        <Download size={14} className="mr-2" />
                        Export All Data (JSON)
                      </Button>
                    </div>
                  </div>
                  <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                    <SectionHeader icon={Database} title="Database Stats" subtitle="Current data counts" />
                    <div className="p-6 grid grid-cols-2 gap-3">
                      {Object.entries(dbStats).map(([key, value]) => (
                        <div key={key} className="p-3 bg-[hsl(var(--surface-elevated))] rounded-lg">
                          <p className="text-xs text-[#6B7280] capitalize">{key.replace('_count', '').replace('_', ' ')}</p>
                          <p className="text-lg font-bold text-white">{value?.toLocaleString() || 0}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* ===== AI BRAIN TAB ===== */}
              <TabsContent value="ai-brain" className="space-y-6">
                <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                  <SectionHeader icon={Upload} title="Upload Kitab" subtitle="Upload your knowledge base document" />
                  <div className="p-6">
                    <input type="file" ref={fileInputRef} onChange={handleKitabUpload} accept=".pdf,.txt,.doc,.docx" className="hidden" />
                    {kitabUploaded ? (
                      <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
                            <FileText size={20} className="text-[#10B981]" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-white">{kitabFile?.name || 'Kitab.pdf'}</p>
                            <p className="text-xs text-[#10B981]">Berjaya dimuat naik</p>
                          </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} className="border-[#10B981]/30 text-[#10B981]">
                          <RefreshCw size={14} className="mr-2" />Ganti Fail
                        </Button>
                      </div>
                    ) : (
                      <div onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed border-[hsl(var(--border))] rounded-xl p-8 text-center cursor-pointer hover:border-[#E63946]/50 hover:bg-[#E63946]/5 transition-all">
                        <Upload size={40} className="mx-auto mb-3 text-[#6B7280]" />
                        <p className="text-sm text-white mb-1">Klik untuk muat naik kitab</p>
                        <p className="text-xs text-[#6B7280]">Sokongan: PDF, TXT, DOC, DOCX</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                  <SectionHeader icon={Brain} title="AI Suggestion Engine" subtitle="Multi-LLM powered insights (GPT-5.2 + Gemini + Claude)" />
                  <div className="p-6 space-y-4">
                    <div>
                      <Label className="text-[#9CA3AF] text-sm mb-2 block">Tanya soalan tentang Marketing, Sales, atau Operation:</Label>
                      <div className="flex gap-3">
                        <Input value={aiQuery} onChange={(e) => setAiQuery(e.target.value)} placeholder="Contoh: Apakah strategi marketing terbaik untuk launch produk baru?" className="flex-1 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" onKeyPress={(e) => e.key === 'Enter' && handleAiQuery()} />
                        <Button onClick={handleAiQuery} disabled={aiLoading} className="bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] text-white">
                          {aiLoading ? <><Loader2 size={14} className="mr-2 animate-spin" />Processing...</> : <><Sparkles size={14} className="mr-2" />Dapatkan Cadangan</>}
                        </Button>
                      </div>
                    </div>
                    {aiResponse && (
                      <div className="space-y-4">
                        <div className="bg-gradient-to-br from-[#8B5CF6]/10 to-[#7C3AED]/5 border border-[#8B5CF6]/30 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Sparkles size={18} className="text-[#8B5CF6]" />
                            <h4 className="text-sm font-bold text-white">Cadangan Akhir (Consolidated)</h4>
                          </div>
                          <p className="text-sm text-[#E5E7EB] whitespace-pre-wrap">{aiResponse.consolidated}</p>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          {aiResponse.responses?.map((resp, idx) => (
                            <div key={idx} className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-3">
                              <Badge className={`text-[10px] mb-2 ${resp.model.includes('gpt') ? 'bg-[#10B981]/20 text-[#10B981]' : resp.model.includes('gemini') ? 'bg-[#3B82F6]/20 text-[#3B82F6]' : 'bg-[#F59E0B]/20 text-[#F59E0B]'}`}>{resp.model}</Badge>
                              <p className="text-xs text-[#9CA3AF] line-clamp-4">{resp.response}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {!aiResponse && !aiLoading && (
                      <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-6 text-center">
                        <Brain size={40} className="mx-auto mb-3 text-[#6B7280]" />
                        <p className="text-[#9CA3AF] text-sm">Masukkan soalan dan tekan &quot;Dapatkan Cadangan&quot;</p>
                        <p className="text-[#6B7280] text-xs mt-2">Sistem akan query 3 AI models dan consolidate jawapan terbaik</p>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* ===== REPORTS TAB ===== */}
              <TabsContent value="reports" className="space-y-6">
                <AdminReportsTab />
              </TabsContent>

              <TabsContent value="tg-reports" className="space-y-6">
                <TelegramReportsTab />
              </TabsContent>

              <TabsContent value="onboarding" className="space-y-6">
                <OnboardingAdmin users={users} />
              </TabsContent>

              <TabsContent value="scorecard" className="space-y-6">
                <AdminScorecardTab />
              </TabsContent>

              <TabsContent value="data-quality" className="space-y-6">
                <DataQualityDashboard />
              </TabsContent>

              <TabsContent value="recovery" className="space-y-6">
                <DataRecoveryDashboard />
              </TabsContent>

              <TabsContent value="backup" className="space-y-6">
                <AdminBackupTab />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>

      {/* ===== MODALS ===== */}
      
      {/* Add User Modal */}
      <Dialog open={showAddUserModal} onOpenChange={setShowAddUserModal}>
        <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus size={20} className="text-[#E63946]" />Tambah User Baru</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-[#9CA3AF] text-sm">Nama</Label>
              <Input 
                value={newUserForm.name} 
                onChange={(e) => setNewUserForm({...newUserForm, name: e.target.value})} 
                placeholder="Nama penuh" 
                className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" 
              />
            </div>
            <div>
              <Label className="text-[#9CA3AF] text-sm">Email</Label>
              <Input 
                type="email"
                value={newUserForm.email} 
                onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})} 
                placeholder="email@underads.com" 
                className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" 
              />
            </div>
            <div>
              <Label className="text-[#9CA3AF] text-sm">Password</Label>
              <Input 
                value={newUserForm.password} 
                onChange={(e) => setNewUserForm({...newUserForm, password: e.target.value})} 
                placeholder="Minimum 6 characters" 
                className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" 
              />
            </div>
            <div>
              <Label className="text-[#9CA3AF] text-sm">Role</Label>
              <Select value={newUserForm.role_id} onValueChange={(v) => setNewUserForm({...newUserForm, role_id: v})}>
                <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => setShowAddUserModal(false)}>Batal</Button>
            <Button onClick={handleAddUser} className="bg-[#E63946] text-white">
              <UserPlus size={14} className="mr-1" /> Tambah User
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* PIN Change Modal */}
      <Dialog open={showPinChange} onOpenChange={setShowPinChange}>
        <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Key size={20} className="text-[#E63946]" />Tukar PIN Admin</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-[#9CA3AF] text-sm">PIN Baru</Label>
              <div className="relative">
                <Input type={showPin ? 'text' : 'password'} value={newPin} onChange={(e) => setNewPin(e.target.value)} placeholder="Masukkan PIN baru" className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white pr-10" />
                <button type="button" onClick={() => setShowPin(!showPin)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280]">{showPin ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
            <div>
              <Label className="text-[#9CA3AF] text-sm">Sahkan PIN</Label>
              <Input type={showPin ? 'text' : 'password'} value={confirmPin} onChange={(e) => setConfirmPin(e.target.value)} placeholder="Masukkan semula PIN" className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" />
            </div>
            <Button onClick={handlePinChange} className="w-full bg-gradient-to-r from-[#E63946] to-[#DC2626] text-white"><Save size={14} className="mr-2" />Simpan PIN Baru</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Role Modal */}
      <RoleModal open={showRoleModal} onOpenChange={setShowRoleModal} role={selectedRole} onSave={handleSaveRole} />
      
      {/* Integration Modal */}
      <IntegrationModal open={showIntegrationModal} onOpenChange={setShowIntegrationModal} integration={selectedIntegration} onSave={handleUpdateIntegration} onTest={handleTestIntegration} />
      
      {/* IB Modal */}
      <IBModal open={showIBModal} onOpenChange={setShowIBModal} ib={selectedIB} onSave={handleCreateIB} />
      
      {/* Permissions Modal */}
      <Dialog open={showPermModal} onOpenChange={setShowPermModal}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Shield size={18} className="text-[hsl(var(--primary))]" /> Permissions — {permEditUser?.name}</DialogTitle>
          </DialogHeader>
          {permEditUser && (() => {
            const roleDefaults = {
              admin: { dashboard: true, ib_management: true, team_space: true, finance_access: true, finance_approve: true, admin_panel: true, channel_settings: true, resource_library: true },
              manager: { dashboard: true, ib_management: true, team_space: true, finance_access: true, finance_approve: false, admin_panel: false, channel_settings: true, resource_library: true },
              developer: { dashboard: true, ib_management: true, team_space: true, finance_access: true, finance_approve: false, admin_panel: true, channel_settings: true, resource_library: true },
              executive: { dashboard: true, ib_management: true, team_space: true, finance_access: false, finance_approve: false, admin_panel: false, channel_settings: false, resource_library: true },
            };
            const role = (permEditUser.role_id || 'viewer').toLowerCase();
            const defaults = roleDefaults[role] || {};
            const up = permEditUser.permissions || {};
            const getVal = (key) => up[key] !== undefined ? up[key] : (defaults[key] || false);
            const setVal = (key, val) => {
              const newPerms = { ...(permEditUser.permissions || {}), [key]: val };
              handleUpdateUser(permEditUser.id, { permissions: newPerms });
              setPermEditUser({ ...permEditUser, permissions: newPerms });
              if (key.startsWith('ts_')) {
                toast.info('User perlu re-login untuk nampak perubahan sidebar', { duration: 3000 });
              }
            };
            const [teamExpanded, setTeamExpanded] = [permTeamExpanded, setPermTeamExpanded];
            return (
              <div className="space-y-4 py-2">
                <p className="text-xs text-[hsl(var(--text-muted))]">Role: <span className="font-medium text-[hsl(var(--text-secondary))]">{role}</span> — checkboxes override role defaults</p>
                
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wider mb-2">Module Access</p>
                  {[
                    { key: 'dashboard', label: 'Dashboard & Overall Performance' },
                    { key: 'ib_management', label: 'IB Management (List, Details, Data)' },
                  ].map(p => (
                    <label key={p.key} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[hsl(var(--surface))] cursor-pointer">
                      <input type="checkbox" checked={getVal(p.key)} onChange={(e) => setVal(p.key, e.target.checked)} className="w-4 h-4 rounded accent-[hsl(var(--primary))]" />
                      <span className="text-sm">{p.label}</span>
                    </label>
                  ))}
                  
                  {/* Team Space with sub-toggles */}
                  <div>
                    <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-[hsl(var(--surface))]">
                      <input type="checkbox" checked={getVal('team_space')} onChange={(e) => {
                        const val = e.target.checked;
                        // When enabling team_space, initialize all sub-pages
                        const tsKeys = ['ts_announcements', 'ts_mood', 'ts_ideas', 'ts_creative', 'ts_voting', 'ts_standup', 'ts_leaderboard'];
                        const newPerms = { ...(permEditUser.permissions || {}), team_space: val };
                        if (val) {
                          // Set all sub-pages to true by default when first enabling
                          tsKeys.forEach(k => { if (newPerms[k] === undefined) newPerms[k] = true; });
                        }
                        handleUpdateUser(permEditUser.id, { permissions: newPerms });
                        setPermEditUser({ ...permEditUser, permissions: newPerms });
                      }} className="w-4 h-4 rounded accent-[hsl(var(--primary))]" />
                      <button onClick={() => setTeamExpanded(!teamExpanded)} className="flex-1 flex items-center justify-between text-sm text-left">
                        <span>Team Space</span>
                        <ChevronDown size={14} className={`text-[hsl(var(--text-muted))] transition-transform ${teamExpanded ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                    {teamExpanded && getVal('team_space') && (
                      <div className="ml-9 space-y-0.5 pb-2">
                        {[
                          { key: 'ts_announcements', label: 'Announcements' },
                          { key: 'ts_mood', label: 'Daily Ecstasy' },
                          { key: 'ts_ideas', label: 'Ideas & Feedback' },
                          { key: 'ts_creative', label: 'Creative Request' },
                          { key: 'ts_voting', label: 'Voting & Polls' },
                          { key: 'ts_standup', label: 'Weekly Standup' },
                          { key: 'ts_leaderboard', label: 'Leaderboard' },
                        ].map(sub => (
                          <label key={sub.key} className="flex items-center gap-2.5 p-1.5 rounded hover:bg-[hsl(var(--surface))] cursor-pointer">
                            <input type="checkbox" checked={getVal(sub.key) === true} onChange={(e) => setVal(sub.key, e.target.checked)} className="w-3.5 h-3.5 rounded accent-emerald-500" />
                            <span className="text-xs text-[hsl(var(--text-secondary))]">{sub.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {[
                    { key: 'finance_access', label: 'Finance (Fund Request, Marketing Fund)' },
                    { key: 'finance_approve', label: 'Finance — Can Approve/Reject' },
                    { key: 'resource_library', label: 'UA Resource Library' },
                    { key: 'channel_settings', label: 'Channel Settings (Targets, KPI)' },
                    { key: 'admin_panel', label: 'Admin Panel' },
                  ].map(p => (
                    <label key={p.key} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[hsl(var(--surface))] cursor-pointer">
                      <input type="checkbox" checked={getVal(p.key)} onChange={(e) => setVal(p.key, e.target.checked)} className="w-4 h-4 rounded accent-[hsl(var(--primary))]" />
                      <span className="text-sm">{p.label}</span>
                    </label>
                  ))}
                </div>
                
                <div className="space-y-2 pt-3 border-t border-[hsl(var(--border))]">
                  <p className="text-xs font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wider">Channel Access</p>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-[hsl(var(--surface))] cursor-pointer">
                      <input type="radio" name="channel_access" checked={(up.channel_access || 'all') === 'all'} onChange={() => { setVal('channel_access', 'all'); }} className="w-4 h-4 accent-[hsl(var(--primary))]" />
                      <div><p className="text-sm font-medium">All Channels</p><p className="text-[10px] text-[hsl(var(--text-muted))]">Boleh access semua IB channels</p></div>
                    </label>
                    <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-[hsl(var(--surface))] cursor-pointer">
                      <input type="radio" name="channel_access" checked={up.channel_access === 'specific'} onChange={() => { setVal('channel_access', 'specific'); }} className="w-4 h-4 accent-[hsl(var(--primary))]" />
                      <div><p className="text-sm font-medium">Specific Channels Only</p><p className="text-[10px] text-[hsl(var(--text-muted))]">Hanya nampak channel yang ditick</p></div>
                    </label>
                  </div>
                  
                  {up.channel_access === 'specific' && (
                    <div className="ml-6 space-y-1 max-h-40 overflow-y-auto">
                      {ibList.map(ch => (
                        <label key={ch.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-[hsl(var(--surface))] cursor-pointer">
                          <input type="checkbox" checked={(up.allowed_channels || []).includes(ch.id)}
                            onChange={(e) => {
                              const current = up.allowed_channels || [];
                              const newChannels = e.target.checked ? [...current, ch.id] : current.filter(id => id !== ch.id);
                              setVal('allowed_channels', newChannels);
                            }} className="w-3.5 h-3.5 rounded accent-emerald-500" />
                          <span className="text-xs">{ch.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="pt-3 border-t border-[hsl(var(--border))]">
                  <Button onClick={() => { setShowPermModal(false); toast.success('Permissions saved'); }} className="w-full bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--brand-orange))] text-white">
                    Done
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
    </ResponsiveLayout>
  );
}

// Role Modal Component
function RoleModal({ open, onOpenChange, role, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    can_edit_ib: false,
    can_edit_campaign: false,
    can_edit_daily_data: false,
    can_edit_settings: false,
    can_access_admin: false,
  });

  useEffect(() => {
    if (role) {
      setFormData(role);
    } else {
      setFormData({ name: '', can_edit_ib: false, can_edit_campaign: false, can_edit_daily_data: false, can_edit_settings: false, can_access_admin: false });
    }
  }, [role, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white max-w-md">
        <DialogHeader>
          <DialogTitle>{role ? 'Edit Role' : 'Create New Role'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label className="text-[#9CA3AF] text-sm">Role Name</Label>
            <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" />
          </div>
          <div className="space-y-3">
            <Label className="text-[#9CA3AF] text-sm">Permissions (Edit Access)</Label>
            {[
              { key: 'can_edit_ib', label: 'Edit IB' },
              { key: 'can_edit_campaign', label: 'Edit Campaign' },
              { key: 'can_edit_daily_data', label: 'Edit Daily Data' },
              { key: 'can_edit_settings', label: 'Edit Settings' },
              { key: 'can_access_admin', label: 'Admin Panel Access' },
            ].map((perm) => (
              <div key={perm.key} className="flex items-center justify-between">
                <span className="text-sm text-white">{perm.label}</span>
                <Switch checked={formData[perm.key]} onCheckedChange={(v) => setFormData({...formData, [perm.key]: v})} />
              </div>
            ))}
          </div>
          <Button onClick={() => onSave(formData)} className="w-full bg-gradient-to-r from-[#E63946] to-[#DC2626] text-white">
            <Save size={14} className="mr-2" />Save Role
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Integration Modal Component
function IntegrationModal({ open, onOpenChange, integration, onSave, onTest }) {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (integration) {
      setFormData(integration);
    }
  }, [integration, open]);

  if (!integration) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 size={20} className="text-[#E63946]" />
            Configure Integration
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6 mt-4">
          {/* IB Name Edit */}
          <div className="p-4 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">🏷️</span>
              <h4 className="text-sm font-semibold text-white">IB/Channel Name</h4>
            </div>
            <div className="space-y-2">
              <Input 
                value={formData.ib_name || ''} 
                onChange={(e) => setFormData({...formData, ib_name: e.target.value})} 
                placeholder="Enter IB/Channel name" 
                className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-sm font-medium" 
              />
              <p className="text-xs text-[#6B7280]">ID: {integration.ib_id}</p>
            </div>
          </div>

          {/* Google Sheets */}
          <div className="p-4 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">📊</span>
              <h4 className="text-sm font-semibold text-white">Google Sheets (Performance Data)</h4>
              <StatusBadge status={formData.gsheet_performance_status} />
            </div>
            <div className="space-y-2">
              <Input value={formData.gsheet_performance_url || ''} onChange={(e) => setFormData({...formData, gsheet_performance_url: e.target.value})} placeholder="https://docs.google.com/spreadsheets/d/..." className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-sm" />
              <Button variant="outline" size="sm" onClick={() => onTest(integration.ib_id, 'gsheet')} className="text-xs"><TestTube size={12} className="mr-1" />Test Connection</Button>
            </div>
          </div>

          {/* Telegram/tgstat */}
          <div className="p-4 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">📱</span>
              <h4 className="text-sm font-semibold text-white">Telegram / tgstat (Channel Analytics)</h4>
              <StatusBadge status={formData.telegram_status} />
            </div>
            <div className="space-y-2">
              <Input value={formData.telegram_tgstat_api || ''} onChange={(e) => setFormData({...formData, telegram_tgstat_api: e.target.value})} placeholder="tgstat API Key" className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-sm" />
              <Input value={formData.telegram_bot_token || ''} onChange={(e) => setFormData({...formData, telegram_bot_token: e.target.value})} placeholder="Telegram Bot Token" className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-sm" />
              <Input value={formData.telegram_chat_id || ''} onChange={(e) => setFormData({...formData, telegram_chat_id: e.target.value})} placeholder="Chat ID" className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-sm" />
              <Button variant="outline" size="sm" onClick={() => onTest(integration.ib_id, 'telegram')} className="text-xs"><TestTube size={12} className="mr-1" />Test Connection</Button>
            </div>
          </div>

          {/* Broker API */}
          <div className="p-4 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">💹</span>
              <h4 className="text-sm font-semibold text-white">Broker API (Database Segmentation)</h4>
              <StatusBadge status={formData.broker_status} />
            </div>
            <div className="space-y-2">
              <Select value={formData.broker_name || ''} onValueChange={(v) => setFormData({...formData, broker_name: v})}>
                <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-sm">
                  <SelectValue placeholder="Select Broker" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HFM">HFM</SelectItem>
                  <SelectItem value="OCTA">OCTA</SelectItem>
                  <SelectItem value="VANTAGE">VANTAGE</SelectItem>
                  <SelectItem value="IC MARKETS">IC MARKETS</SelectItem>
                  <SelectItem value="EXNESS">EXNESS</SelectItem>
                  <SelectItem value="XM">XM</SelectItem>
                  <SelectItem value="FXCM">FXCM</SelectItem>
                </SelectContent>
              </Select>
              <Input value={formData.broker_api_key || ''} onChange={(e) => setFormData({...formData, broker_api_key: e.target.value})} placeholder="API Key" className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-sm" />
              <Input type="password" value={formData.broker_api_secret || ''} onChange={(e) => setFormData({...formData, broker_api_secret: e.target.value})} placeholder="API Secret" className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-sm" />
              <Button variant="outline" size="sm" onClick={() => onTest(integration.ib_id, 'broker')} className="text-xs"><TestTube size={12} className="mr-1" />Test Connection</Button>
            </div>
          </div>

          {/* Webhook */}
          <div className="p-4 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">🔗</span>
              <h4 className="text-sm font-semibold text-white">Webhook</h4>
              <StatusBadge status={formData.webhook_status} />
            </div>
            <div className="space-y-2">
              <Input value={formData.webhook_url || ''} onChange={(e) => setFormData({...formData, webhook_url: e.target.value})} placeholder="https://your-webhook-url.com/..." className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-sm" />
              <Button variant="outline" size="sm" onClick={() => onTest(integration.ib_id, 'webhook')} className="text-xs"><TestTube size={12} className="mr-1" />Test Connection</Button>
            </div>
          </div>

          <Button onClick={() => onSave(integration.ib_id, formData)} className="w-full bg-gradient-to-r from-[#E63946] to-[#DC2626] text-white">
            <Save size={14} className="mr-2" />Save Integration Settings
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// IB Modal Component
function IBModal({ open, onOpenChange, ib, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    monthly_target_commission: 0,
    monthly_target_deposit: 0,
    monthly_target_lot: 0,
    monthly_target_active_client: 0,
  });

  useEffect(() => {
    if (ib) {
      setFormData(ib);
    } else {
      setFormData({ name: '', monthly_target_commission: 0, monthly_target_deposit: 0, monthly_target_lot: 0, monthly_target_active_client: 0 });
    }
  }, [ib, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white max-w-md">
        <DialogHeader>
          <DialogTitle>{ib ? 'Edit IB' : 'Create New IB'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label className="text-[#9CA3AF] text-sm">IB Name</Label>
            <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#9CA3AF] text-xs">Target Commission (RM)</Label>
              <Input type="number" value={formData.monthly_target_commission} onChange={(e) => setFormData({...formData, monthly_target_commission: parseFloat(e.target.value)})} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" />
            </div>
            <div>
              <Label className="text-[#9CA3AF] text-xs">Target Deposit (RM)</Label>
              <Input type="number" value={formData.monthly_target_deposit} onChange={(e) => setFormData({...formData, monthly_target_deposit: parseFloat(e.target.value)})} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" />
            </div>
            <div>
              <Label className="text-[#9CA3AF] text-xs">Target Lot</Label>
              <Input type="number" value={formData.monthly_target_lot} onChange={(e) => setFormData({...formData, monthly_target_lot: parseFloat(e.target.value)})} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" />
            </div>
            <div>
              <Label className="text-[#9CA3AF] text-xs">Target Active Clients</Label>
              <Input type="number" value={formData.monthly_target_active_client} onChange={(e) => setFormData({...formData, monthly_target_active_client: parseInt(e.target.value)})} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" />
            </div>
          </div>
          <Button onClick={() => onSave(formData)} className="w-full bg-gradient-to-r from-[#E63946] to-[#DC2626] text-white">
            <Save size={14} className="mr-2" />Save IB
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== ONBOARDING ADMIN =====
function OnboardingAdmin({ users = [] }) {
  const [tasks, setTasks] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showAdd, setShowAdd] = React.useState(false);
  const [newTask, setNewTask] = React.useState({ label: '', category: 'general', required: false, assign_to: 'all', assign_roles: [], assign_users: [] });
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const h = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

  const fetchTasks = async () => {
    try { const r = await axios.get(`${API}/admin/onboarding-tasks`, h); setTasks(r.data.tasks || []); } catch {}
    finally { setLoading(false); }
  };
  React.useEffect(() => { fetchTasks(); }, []);

  const handleCreate = async () => {
    if (!newTask.label.trim()) return;
    try {
      await axios.post(`${API}/admin/onboarding-tasks`, newTask, { ...h, headers: { ...h.headers, 'Content-Type': 'application/json' } });
      setNewTask({ label: '', category: 'general', required: false, assign_to: 'all', assign_roles: [], assign_users: [] });
      setShowAdd(false);
      fetchTasks();
      toast.success('Onboarding task created & assigned!');
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/admin/onboarding-tasks/${id}`, h); fetchTasks(); toast.success('Deleted'); } catch {}
  };

  const categories = [
    { value: 'profile', label: 'Profile Setup' },
    { value: 'security', label: 'Security' },
    { value: 'channel', label: 'Channel Setup' },
    { value: 'data', label: 'Data Entry' },
    { value: 'task', label: 'Getting Started' },
    { value: 'general', label: 'General' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <SectionHeader icon={Rocket} title="Onboarding Tasks" subtitle="Manage setup checklist for new & existing users" />
        <Button onClick={() => setShowAdd(true)} className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white"><Plus size={16} className="mr-1" /> Add Task</Button>
      </div>

      {loading ? <p className="text-center text-[#6B7280] py-8">Loading...</p> : (
        <div className="space-y-2">
          {tasks.length === 0 && <p className="text-center text-[#6B7280] py-8">No custom onboarding tasks. Default tasks auto-generated by role.</p>}
          {tasks.map(t => (
            <div key={t.id} className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg p-4 flex items-center gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium">{t.label}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className="bg-violet-500/10 text-violet-400 border border-violet-500/20 text-[9px]">{t.category}</Badge>
                  {t.required && <Badge className="bg-red-500/10 text-red-400 border border-red-500/20 text-[9px]">WAJIB</Badge>}
                  <span className="text-[10px] text-[#6B7280]">
                    {t.assign_to === 'all' ? 'All users' : t.assign_to === 'roles' ? `Roles: ${(t.assign_roles||[]).join(', ')}` : `${(t.assign_users||[]).length} user(s)`}
                  </span>
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => handleDelete(t.id)}><Trash2 size={14} /></Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Rocket size={18} className="text-emerald-400" /> New Onboarding Task</DialogTitle></DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Task Description *</label>
              <Input value={newTask.label} onChange={e => setNewTask({...newTask, label: e.target.value})} placeholder="e.g. Upload profile picture" className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Category</label>
                <Select value={newTask.category} onValueChange={v => setNewTask({...newTask, category: v})}>
                  <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--card))]">{categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 p-2.5 rounded-lg border border-[hsl(var(--border))] cursor-pointer w-full">
                  <input type="checkbox" checked={newTask.required} onChange={e => setNewTask({...newTask, required: e.target.checked})} className="w-4 h-4 accent-red-500" />
                  <span className="text-sm">Wajib</span>
                </label>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Assign To</label>
              <Select value={newTask.assign_to} onValueChange={v => setNewTask({...newTask, assign_to: v})}>
                <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))]">
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="roles">By Role</SelectItem>
                  <SelectItem value="users">Specific Users</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newTask.assign_to === 'roles' && (
              <div className="flex flex-wrap gap-2">
                {['admin','manager','executive','developer'].map(r => (
                  <label key={r} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[hsl(var(--border))] cursor-pointer text-xs">
                    <input type="checkbox" checked={(newTask.assign_roles||[]).includes(r)} onChange={e => {
                      const roles = e.target.checked ? [...(newTask.assign_roles||[]), r] : (newTask.assign_roles||[]).filter(x=>x!==r);
                      setNewTask({...newTask, assign_roles: roles});
                    }} className="w-3 h-3 accent-emerald-500" />
                    {r}
                  </label>
                ))}
              </div>
            )}
            {newTask.assign_to === 'users' && (
              <div className="max-h-32 overflow-y-auto space-y-1">
                {users.map(u => (
                  <label key={u.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-[hsl(var(--surface))] cursor-pointer">
                    <input type="checkbox" checked={(newTask.assign_users||[]).includes(u.id)} onChange={e => {
                      const ids = e.target.checked ? [...(newTask.assign_users||[]), u.id] : (newTask.assign_users||[]).filter(x=>x!==u.id);
                      setNewTask({...newTask, assign_users: ids});
                    }} className="w-3 h-3 accent-emerald-500" />
                    <span className="text-xs">{u.name} ({u.role_id})</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowAdd(false)} className="flex-1">Cancel</Button>
            <Button onClick={handleCreate} className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white">Create & Assign</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

